import React, { useEffect, useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { ConsultationTypeBadge } from '../../components/ui/ConsultationTypeBadge';
import { Encounter, Patient, ClinicalNote, LabRequest, RadiologyRequest, Prescription } from '../../types';
import { Stethoscope, FileText, FlaskConical, Scan, Pill, CheckCircle, History, X, ZoomIn, ZoomOut, Eye, Printer, Download, Trash2, RotateCcw } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { SickNoteForm } from './SickNoteForm';
/**
 * Capture a DOM element and export it as a properly paginated A4 PDF.
 * A4 layout: 210mm × 297mm, margins 20mm top/bottom, 18mm left/right.
 */
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isAMHTest, hcgStructure, isHCGTest, isHCGAbnormal, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest } from '../../store/labStructures';
type CViewingTest = {
  kind: 'lab';
  labId: string;
  testName?: string;
} | {
  kind: 'rad';
  radId: string;
} | null;
export const ClinicianDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters,
    logAudit
  } = useData();
  const [activeTab, setActiveTab] = useState<'queue' | 'completed'>('queue');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);
  // Live encounter - always reads from context so lab/rad updates appear instantly
  const selectedEncounter = selectedEncounterId ? encounters.find(e => e.id === selectedEncounterId) || null : null;
  // Image zoom state
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  // View Test File modal state
  const [viewingTest, setViewingTest] = useState<CViewingTest>(null);
  const [testImageZoom, setTestImageZoom] = useState<number>(100);
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);
  const testModalRef = useRef<HTMLDivElement>(null);
  // Remove Test Modal state
  const [removeTestModal, setRemoveTestModal] = useState<{
    isOpen: boolean;
    labId: string;
    testName: string;
    reason: string;
  }>({
    isOpen: false,
    labId: '',
    testName: '',
    reason: ''
  });
  const closeTestModal = () => {
    setViewingTest(null);
    setTestImageZoom(100);
  };
  const handlePrintTestModal = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadTestPdf = async () => {
    if (!testModalRef.current) return;
    setIsGeneratingTestPdf(true);
    try {
      const filename = `BMED_TestResult_${Date.now()}.pdf`;
      await captureToA4Pdf(testModalRef.current, filename);
    } catch (err) {
      console.error('Test PDF failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingTestPdf(false);
    }
  };
  // Modals state
  const [showLabModal, setShowLabModal] = useState(false);
  const [showRadModal, setShowRadModal] = useState(false);
  const [showRxModal, setShowRxModal] = useState(false);
  const [showSickNoteModal, setShowSickNoteModal] = useState(false);
  // Clinical Notes State
  const [notes, setNotes] = useState<Partial<ClinicalNote>>({
    presentingComplaint: '',
    historyOfPresentingComplaint: '',
    pastMedicalHistory: [],
    drugHistory: '',
    familyMedicalHistory: [],
    obgynHistory: '',
    socialHistory: {
      alcohol: '',
      smoking: '',
      occupation: '',
      mentalStatus: ''
    },
    reviewOfSystems: {
      rs: '',
      cvs: '',
      cns: '',
      git: '',
      gut: '',
      mss: '',
      is: ''
    },
    onExamination: '',
    onExaminationDetails: {
      gc: '',
      febrile: '',
      pjc: '',
      s1s2: '',
      rs: '',
      pa: ''
    },
    significantFindings: [],
    workingDiagnosis: []
  });
  const [reviewDateNotes, setReviewDateNotes] = useState('');
  // Lab Request State
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [otherTests, setOtherTests] = useState('');
  // Rad Request State
  const [scanType, setScanType] = useState('');
  // Rx State
  const [rxForm, setRxForm] = useState({
    drugName: '',
    dose: '',
    route: '',
    frequency: '',
    days: ''
  });
  // Active queue only. Completed visits live in Patient Files and can be
  // reopened from there via the "Amend File" flow.
  const pendingEncounters = encounters.filter(e => e.stage !== 'COMPLETED');
  const completedEncounters = encounters.filter(e => e.stage === 'COMPLETED');
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const filteredCompleted = completedEncounters.filter(enc => {
    const p = getPatient(enc.patientId);
    if (!p) return false;
    const q = searchQuery.toLowerCase();
    return p.firstName.toLowerCase().includes(q) || p.lastName.toLowerCase().includes(q) || p.bmcFileNumber.toLowerCase().includes(q);
  }).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounterId(encounter.id);
    setReviewDateNotes(encounter.reviewDateNotes || '');
    if (encounter.clinicalNote) {
      setNotes(encounter.clinicalNote);
    } else {
      setNotes({
        presentingComplaint: '',
        historyOfPresentingComplaint: '',
        pastMedicalHistory: [],
        drugHistory: '',
        familyMedicalHistory: [],
        obgynHistory: '',
        socialHistory: {
          alcohol: '',
          smoking: '',
          occupation: '',
          mentalStatus: ''
        },
        reviewOfSystems: {
          rs: '',
          cvs: '',
          cns: '',
          git: '',
          gut: '',
          mss: '',
          is: ''
        },
        onExamination: '',
        onExaminationDetails: {
          gc: '',
          febrile: '',
          pjc: '',
          s1s2: '',
          rs: '',
          pa: ''
        },
        significantFindings: [],
        workingDiagnosis: []
      });
    }
    // Move encounter back to CLINICIAN stage if returning from investigations
    if (encounter.stage === 'INVESTIGATIONS') {
      updateEncounter(encounter.id, {
        stage: 'CLINICIAN',
        lastAttendingClinician: currentUser?.fullName
      });
    } else if (currentUser && encounter.lastAttendingClinician !== currentUser.fullName) {
      // Record that this clinician opened/consulted the file.
      updateEncounter(encounter.id, {
        lastAttendingClinician: currentUser.fullName
      });
    }
  };
  const handleSaveNotes = () => {
    if (!selectedEncounter || !currentUser) return;
    const isAmending = selectedEncounter.stage === 'COMPLETED';
    let editHistory = selectedEncounter.clinicalNote?.editHistory || [];
    if (isAmending) {
      const reason = prompt('Please enter a mandatory reason for modifying this completed consultation:');
      if (!reason || !reason.trim()) {
        alert('A reason is required to amend a completed consultation.');
        return;
      }
      const previousNote = selectedEncounter.clinicalNote!;
      const {
        editHistory: _oldHistory,
        ...noteSnapshot
      } = previousNote;
      editHistory = [...editHistory, {
        editedAt: new Date().toISOString(),
        editedBy: currentUser.id,
        editedByName: currentUser.fullName,
        reason: reason.trim(),
        previousNote: noteSnapshot as any
      }];
      logAudit({
        type: 'consultation.edit',
        encounterId: selectedEncounter.id,
        patientId: selectedEncounter.patientId,
        patientName: `${getPatient(selectedEncounter.patientId)?.firstName} ${getPatient(selectedEncounter.patientId)?.lastName}`,
        userId: currentUser.id,
        userName: currentUser.fullName,
        userRole: currentUser.role,
        reason: reason.trim(),
        previousContent: JSON.stringify(noteSnapshot),
        newContent: JSON.stringify(notes)
      });
    }
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: isAmending ? selectedEncounter.clinicalNote!.recordedAt : new Date().toISOString(),
      recordedBy: isAmending ? selectedEncounter.clinicalNote!.recordedBy : currentUser.id,
      clinicianName: isAmending ? selectedEncounter.clinicalNote!.clinicianName : currentUser.fullName,
      editHistory
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName
    });
    alert(isAmending ? 'Amendment saved and logged successfully.' : 'Clinical notes saved successfully.');
  };
  const handleRequestLab = () => {
    if (!selectedEncounter || !currentUser || selectedTests.length === 0 && !otherTests) return;
    const newLab: LabRequest = {
      id: `lab-${Date.now()}`,
      tests: selectedTests,
      otherTests,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      labRequests: [...selectedEncounter.labRequests, newLab],
      stage: 'INVESTIGATIONS',
      pendingLab: true
    });
    setShowLabModal(false);
    setSelectedTests([]);
    setOtherTests('');
    // File stays open — clinician closes/saves/completes when ready.
    alert('Laboratory tests requested. The patient file remains open — you can continue your consultation.');
  };
  const handleRequestRad = () => {
    if (!selectedEncounter || !currentUser || !scanType) return;
    const newRad: RadiologyRequest = {
      id: `rad-${Date.now()}`,
      scanType,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: [...selectedEncounter.radiologyRequests, newRad],
      stage: 'INVESTIGATIONS',
      pendingRadiology: true
    });
    setShowRadModal(false);
    setScanType('');
    // File stays open — clinician closes/saves/completes when ready.
    alert('Radiology scan requested. The patient file remains open — you can continue your consultation.');
  };
  const handleAddRx = () => {
    if (!selectedEncounter || !currentUser || !rxForm.drugName) return;
    if (selectedEncounter.prescriptions.length >= 20) {
      alert('You have reached the maximum of 20 drugs per prescription. Remove an existing item before adding another.');
      return;
    }
    const newRx: Prescription = {
      id: `rx-${Date.now()}`,
      ...rxForm,
      status: 'Pending',
      prescribedAt: new Date().toISOString(),
      prescribedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      prescriptions: [...selectedEncounter.prescriptions, newRx]
    });
    setRxForm({
      drugName: '',
      dose: '',
      route: '',
      frequency: '',
      days: ''
    });
  };
  // Delete a single prescription. Only allowed while it is still Pending —
  // once Pharmacy has dispensed (or marked unavailable), it stays on record.
  const handleDeletePrescription = (rxId: string) => {
    if (!selectedEncounter) return;
    const rx = selectedEncounter.prescriptions.find(p => p.id === rxId);
    if (!rx) return;
    if (rx.status !== 'Pending') {
      alert('This drug has already been processed by Pharmacy and cannot be removed from the record.');
      return;
    }
    if (!confirm(`Remove "${rx.drugName}" from the prescription list? This cannot be undone.`)) return;
    updateEncounter(selectedEncounter.id, {
      prescriptions: selectedEncounter.prescriptions.filter(p => p.id !== rxId)
    });
  };
  // Recall (remove) a single ordered lab test that has not yet been completed.
  // - For tests inside lab.tests[]: drop just that test from the array.
  // - If that empties the request (and no otherTests), remove the whole request.
  // - For an "other test" entry: clear lab.otherTests on that request, or
  //   remove the request entirely if it has no tests left either.
  const handleRecallLabTest = (labId: string, testName: string, isOther = false) => {
    if (!selectedEncounter) return;
    const lab = selectedEncounter.labRequests.find(l => l.id === labId);
    if (!lab) return;
    if (lab.status === 'Completed') {
      alert('This test has already been completed by the laboratory and cannot be recalled.');
      return;
    }
    if (!confirm(`Recall the lab test "${testName}"? The laboratory will no longer see this request.`)) return;
    let updatedLabs = selectedEncounter.labRequests.map(l => {
      if (l.id !== labId) return l;
      if (isOther) return {
        ...l,
        otherTests: ''
      };
      return {
        ...l,
        tests: l.tests.filter(t => t !== testName)
      };
    });
    // Drop any request that now has no tests AND no otherTests.
    updatedLabs = updatedLabs.filter(l => l.tests.length > 0 || l.otherTests.trim().length > 0);
    const stillPendingLab = updatedLabs.some(l => l.status === 'Pending');
    updateEncounter(selectedEncounter.id, {
      labRequests: updatedLabs,
      pendingLab: stillPendingLab
    });
  };
  // Recall (remove) a pending radiology request.
  const handleRecallRadiology = (radId: string) => {
    if (!selectedEncounter) return;
    const rad = selectedEncounter.radiologyRequests.find(r => r.id === radId);
    if (!rad) return;
    if (rad.status === 'Completed') {
      alert('This scan has already been completed by radiology and cannot be recalled.');
      return;
    }
    if (!confirm(`Recall the radiology request "${rad.scanType}"? Radiology will no longer see this request.`)) return;
    const updatedRads = selectedEncounter.radiologyRequests.filter(r => r.id !== radId);
    const stillPendingRad = updatedRads.some(r => r.status === 'Pending');
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: updatedRads,
      pendingRadiology: stillPendingRad
    });
  };
  const handleRemoveTestConfirm = () => {
    if (!selectedEncounter || !currentUser || !removeTestModal.reason.trim()) return;
    const lab = selectedEncounter.labRequests.find(l => l.id === removeTestModal.labId);
    if (!lab) return;
    const now = new Date().toISOString();
    const newEntry = {
      testName: removeTestModal.testName,
      removedAt: now,
      removedBy: currentUser.id,
      reason: removeTestModal.reason
    };
    const updatedLabRequests = selectedEncounter.labRequests.map(l => {
      if (l.id !== removeTestModal.labId) return l;
      return {
        ...l,
        removedTestNames: [...(l.removedTestNames || []), removeTestModal.testName],
        removedTestEntries: [...(l.removedTestEntries || []), newEntry]
      };
    });
    updateEncounter(selectedEncounter.id, {
      labRequests: updatedLabRequests
    });
    const patient = patients.find(p => p.id === selectedEncounter.patientId);
    logAudit({
      type: 'lab.test.remove',
      encounterId: selectedEncounter.id,
      patientId: selectedEncounter.patientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown',
      userId: currentUser.id,
      userName: currentUser.fullName,
      userRole: currentUser.role,
      reason: removeTestModal.reason,
      labRequestId: removeTestModal.labId,
      testName: removeTestModal.testName
    });
    setRemoveTestModal({
      isOpen: false,
      labId: '',
      testName: '',
      reason: ''
    });
  };
  const handleCompleteVisit = () => {
    if (!selectedEncounter || !currentUser) return;
    // Save notes one last time
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    const hasPrescriptions = selectedEncounter.prescriptions.length > 0;
    const hasPendingDispense = selectedEncounter.prescriptions.some(rx => rx.status === 'Pending');
    // If prescriptions exist and none have been dispensed yet, send to pharmacy.
    // If pharmacy has already processed them (returned to clinician), complete the visit.
    const sendToPharmacy = hasPrescriptions && hasPendingDispense;
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName,
      stage: sendToPharmacy ? 'PHARMACY' : 'COMPLETED',
      completedAt: sendToPharmacy ? undefined : new Date().toISOString()
    });
    alert(sendToPharmacy ? 'Visit saved. Patient forwarded to Pharmacy.' : 'Visit completed successfully.');
    setSelectedEncounterId(null);
  };
  const handleSendToPharmacy = () => {
    if (!selectedEncounter || !currentUser) return;
    const pendingRx = selectedEncounter.prescriptions.filter(rx => rx.status === 'Pending');
    if (pendingRx.length === 0) {
      alert('No pending prescriptions to send to pharmacy.');
      return;
    }
    // Save current notes
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName,
      stage: 'PHARMACY'
    });
    // File stays open — clinician decides when to close, save draft, or complete the visit.
    alert(`Prescription sent to Pharmacy (${pendingRx.length} drug${pendingRx.length > 1 ? 's' : ''}). The patient file remains open — you can continue your consultation.`);
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-2085" data-mp="26bf8s6ln2vl9">Patient not found</div>;
    const isCompleted = selectedEncounter.stage === 'COMPLETED';
    const isOriginalClinician = selectedEncounter.clinicalNote?.recordedBy === currentUser?.id;
    const isAdmin = currentUser?.role === 'Administrator';
    const canEdit = !isCompleted || isOriginalClinician || isAdmin;
    return <div className="space-y-6 pb-20" data-id="element-2086" data-mp="1ij8ere3dz0p0">
        {isCompleted && !canEdit && <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded flex items-center mb-4" data-id="element-2087" data-mp="1nibc0cd4k2mf">
            <span className="text-sm font-medium" data-id="element-2088" data-mp="4moz7p7ftwo7">
              This consultation is completed and can only be amended by the
              original clinician (
              {selectedEncounter.clinicalNote?.clinicianName}) or an
              Administrator.
            </span>
          </div>}
        {isCompleted && canEdit && <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded flex items-center mb-4" data-id="element-2089" data-mp="3hvtbna51m4ie">
            <History className="w-5 h-5 mr-2 text-amber-600" data-id="element-2090" data-mp="3d41mn41jc6ud" />
            <span className="text-sm font-medium" data-id="element-2091" data-mp="392q3nhd62jv5">
              You are viewing a completed consultation. Any edits saved will be
              logged in the audit trail.
            </span>
          </div>}
        {/* Sticky Action Bar */}
        <div className="flex justify-between items-center sticky top-0 bg-gray-100 z-10 py-4 border-b border-gray-200" data-id="element-2092" data-mp="2z4abdb2el4k2">
          <div className="min-w-0" data-id="element-2093" data-mp="2u8bhj7yb2uc9">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-2094" data-mp="2ki3j10wcqq0d">
              {isCompleted ? 'Completed Consultation' : 'Consultation'}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-700" data-id="element-2095" data-mp="2pfd3s38sqdhv">
              <span data-id="element-2096" data-mp="2bjrleex2iryf">
                <span className="text-gray-500" data-id="element-2097" data-mp="2fja0uv64eatv">Patient:</span>{' '}
                <span className="font-semibold text-gray-900" data-id="element-2098" data-mp="2fjjv4uy7745d">
                  {patient.firstName} {patient.lastName}
                </span>
              </span>
              <span className="flex items-center gap-2" data-id="element-2099" data-mp="386w607uzxniu">
                <span className="text-gray-500" data-id="element-2100" data-mp="2ugiyu7pxicuk">Consultation Type:</span>
                {selectedEncounter.consultationType ? <ConsultationTypeBadge type={selectedEncounter.consultationType} data-id="element-2101" data-mp="2yhcnj3b73voi" /> : <span className="italic text-gray-500" data-id="element-2102" data-mp="3m6fb0rsigiqx">Not specified</span>}
              </span>
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-2103" data-mp="1fiy6rsrm5a9i">
            <Button variant="outline" onClick={() => setSelectedEncounterId(null)} data-id="element-2104" data-mp="1fhn5bzln4elk">
              Close File
            </Button>
            <Button variant="outline" className="text-amber-700 border-amber-600 hover:bg-amber-50" onClick={() => {
            const reason = prompt('Send this file back to Nursing so the vitals can be corrected. Please provide a brief reason (optional):', '') ?? '';
            if (confirm('Are you sure you want to send this file back to Nursing? The patient will leave your queue until the nurse re-submits the corrected vitals.')) {
              const notePrefix = reason ? `[Returned to Nursing by ${currentUser?.fullName || 'Clinician'}: ${reason}]` : `[Returned to Nursing by ${currentUser?.fullName || 'Clinician'}]`;
              updateEncounter(selectedEncounter.id, {
                stage: 'NURSING',
                vitalsReturnNote: notePrefix,
                vitalsReturnedAt: new Date().toISOString()
              } as any);
              alert('File sent back to Nursing for vitals correction. It will return to your queue once the nurse re-submits the vitals.');
              setSelectedEncounterId(null);
            }
          }} data-id="element-2105" data-mp="1p8801by70yz7">
              ↩ Send to Nursing
            </Button>
            <Button variant="outline" onClick={() => setShowSickNoteModal(true)} data-id="element-2106" data-mp="1f8rwgqosw44p">
              <FileText className="w-4 h-4 mr-2" data-id="element-2107" data-mp="1o7c9bb7ojjl0" /> Generate Sick Note
            </Button>
            <Button variant="primary" onClick={handleSaveNotes} data-id="element-2108" data-mp="1t66sie9k7a5n">
              {selectedEncounter.stage === 'COMPLETED' ? 'Save Amendment' : 'Save Draft'}
            </Button>
            {selectedEncounter.stage !== 'COMPLETED' && <Button variant="success" className="bg-green-600 text-white hover:bg-green-700" onClick={handleCompleteVisit} data-id="element-2109" data-mp="1y9pnuoek3l4s">
                <CheckCircle className="w-4 h-4 mr-2" data-id="element-2110" data-mp="1pbxa3oohglji" /> Complete Visit
              </Button>}
          </div>
        </div>

        {showSickNoteModal && <SickNoteForm encounter={selectedEncounter} patient={patient} clinician={currentUser} onClose={() => setShowSickNoteModal(false)} data-id="element-2111" data-mp="2h18sezo8b78x" />}

        {/* Medical Form */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6" data-id="element-2112" data-mp="288yh1f1lkvq3">
          <fieldset disabled={!canEdit} className="group" data-id="element-2113" data-mp="34rwagdruzd97">
            {/* ═══ FORM HEADER ═══ */}
            {selectedEncounter.lastAttendingClinician && selectedEncounter.lastAttendingClinician !== currentUser?.fullName && <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded flex items-center" data-id="element-2114" data-mp="131nnld4ac4rz">
                  <Stethoscope className="w-5 h-5 mr-2 text-blue-600" data-id="element-2115" data-mp="obzpslvbbo90" />
                  <span className="text-sm font-medium" data-id="element-2116" data-mp="tbugaob6ebtj">
                    This file was last attended to by:{' '}
                    <span className="font-bold" data-id="element-2117" data-mp="fc33us0b53hn">
                      {selectedEncounter.lastAttendingClinician}
                    </span>
                  </span>
                </div>}
            <div className="flex justify-between items-start mb-6" data-id="element-2118" data-mp="5eyjmxq0vi3h">
              <div className="flex items-center" data-id="element-2119" data-mp="ad9th8gbhslp">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-2120" data-mp="ae1ejpcag7xq" />
                <div data-id="element-2121" data-mp="3p9bh7m5abcbz">
                  <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-2122" data-mp="3p8s5woa53m6x">
                    BMED MEDICAL CENTRE
                  </h1>
                  <p className="text-sm italic text-gray-600" data-id="element-2123" data-mp="3k85nbkzw1j0v">
                    Caring Healing Leading
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-2124" data-mp="3u7mr1wvkxmu7">
                <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-2125" data-mp="3u6lgdtih72d0">
                  Confidential
                </p>
                <p className="text-sm" data-id="element-2126" data-mp="7sadpldw0mg5">
                  <span className="font-semibold" data-id="element-2127" data-mp="lsl46nbabz0s">Date:</span>{' '}
                  {new Date(selectedEncounter.startedAt).toLocaleDateString()}
                </p>
                <p className="text-sm" data-id="element-2128" data-mp="gu1lcvrzn3cn">
                  <span className="font-semibold" data-id="element-2129" data-mp="2njpvfi9b2xkl">File No:</span>{' '}
                  {patient.bmcFileNumber}
                </p>
              </div>
            </div>

            {/* ═══ PERSONAL DETAILS ═══ */}
            <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-2130" data-mp="1ws8zmsktcn0t">
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2131" data-mp="21qskepno7srl">
                <div className="col-span-1 font-semibold" data-id="element-2132" data-mp="21ofv1rfl2g4i">1.</div>
                <div className="col-span-6" data-id="element-2133" data-mp="26i0vdq4nfm6v">
                  <span className="font-semibold" data-id="element-2134" data-mp="2bglt9iwfhts5">Full Names:</span>{' '}
                  {patient.firstName} {patient.lastName}
                </div>
                <div className="col-span-3" data-id="element-2135" data-mp="1dueg3km26t46">
                  <span className="font-semibold" data-id="element-2136" data-mp="1iry7lm7rp2e2">Sex:</span> {patient.sex}
                </div>
                <div className="col-span-2" data-id="element-2137" data-mp="1nr11caiarm6q">
                  <span className="font-semibold" data-id="element-2138" data-mp="1nq817sdufuxk">Age:</span> {patient.age}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2139" data-mp="1srp05icrp7bi">
                <div className="col-span-1 font-semibold" data-id="element-2140" data-mp="2z5ekx360pfgy">2.</div>
                <div className="col-span-11" data-id="element-2141" data-mp="344rh4eis3yw7">
                  <span className="font-semibold" data-id="element-2142" data-mp="343g21ijvv4ps">Marital Status:</span>{' '}
                  {patient.maritalStatus}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2143" data-mp="niksng2iitvd">
                <div className="col-span-1 font-semibold" data-id="element-2144" data-mp="irukxw6m6exi">3.</div>
                <div className="col-span-7" data-id="element-2145" data-mp="iq1dieo6fqog">
                  <span className="font-semibold" data-id="element-2146" data-mp="dtt6jok4v3sd">Home Address:</span>{' '}
                  {patient.address}
                </div>
                <div className="col-span-4" data-id="element-2147" data-mp="8vhtz62gef6g">
                  <span className="font-semibold" data-id="element-2148" data-mp="4sl8n7lj44yt">Contact:</span>{' '}
                  {patient.contact}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2149" data-mp="3vzfowcot1g0h">
                <div className="col-span-1 font-semibold" data-id="element-2150" data-mp="3r0l4j6b6vje9">4.</div>
                <div className="col-span-11" data-id="element-2151" data-mp="1u714n6ysfzgw">
                  <span className="font-semibold" data-id="element-2152" data-mp="1u6rffllkhwc9">Occupation:</span>{' '}
                  {patient.occupation || '_________________________'}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2153" data-mp="2fh2b86pdnrlx">
                <div className="col-span-1 font-semibold" data-id="element-2154" data-mp="2ajslsr1znfy3">5.</div>
                <div className="col-span-5" data-id="element-2155" data-mp="2p7x8050929lm">
                  <span className="font-semibold" data-id="element-2156" data-mp="2p6vn78tfm8qp">Next of Kin:</span>{' '}
                  {patient.nextOfKin}
                </div>
                <div className="col-span-3" data-id="element-2157" data-mp="2kax15rvkt2tg">
                  <span className="font-semibold" data-id="element-2158" data-mp="1wiq1aiqn6dpa">Contact:</span>{' '}
                  {patient.nokContact}
                </div>
                <div className="col-span-3" data-id="element-2159" data-mp="1rlps3yyhwuty">
                  <span className="font-semibold" data-id="element-2160" data-mp="25jxwut1rx0fo">Relationship:</span>{' '}
                  {patient.nokRelationship}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2161" data-mp="21grjl1g8u8zp">
                <div className="col-span-1 font-semibold" data-id="element-2162" data-mp="3gxrw5ycdft8p">6.</div>
                <div className="col-span-11" data-id="element-2163" data-mp="3cy0ydaofgcuo">
                  <span className="font-semibold" data-id="element-2164" data-mp="3cx80z0bcyzp7">Allergies:</span>{' '}
                  <span className="text-red-600 font-medium" data-id="element-2165" data-mp="3kfz557f859u7">
                    {patient.allergies}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2" data-id="element-2166" data-mp="3aj2twtxxy9ly">
                <div className="col-span-1 font-semibold" data-id="element-2167" data-mp="3ake173qlhzht">7.</div>
                <div className="col-span-11" data-id="element-2168" data-mp="3fh4exkexgj02">
                  <span className="font-semibold" data-id="element-2169" data-mp="3fk0ch7mv55nz">HIV Status:</span>{' '}
                  <span className="ml-4" data-id="element-2170" data-mp="73fcuhg4foxj">
                    <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-2171" data-mp="c17c5wj3idbr">
                      Positive {patient.hivStatus === 'Positive' ? '✓' : ''}
                    </span>
                    <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-2172" data-mp="q0yn1oebfdlj">
                      Negative {patient.hivStatus === 'Negative' ? '✓' : ''}
                    </span>
                    <span className={`inline-block px-2 py-0.5 border border-black ${patient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-2173" data-mp="zou5xoxzhjnj">
                      Unknown {patient.hivStatus === 'Unknown' ? '✓' : ''}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* ═══ NURSING OBSERVATIONS ═══ */}
            <div className="mb-6" data-id="element-2174" data-mp="gx1mal4t1ash">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-2175" data-mp="gvzydg2owttc">
                Nursing Observations
              </h3>
              <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-2176" data-mp="7ypnhvtsb6oy">
                <thead data-id="element-2177" data-mp="2x0bacutsmza">
                  <tr className="bg-gray-50" data-id="element-2178" data-mp="2xjwq3jopti2">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-2179" data-mp="3u6z1kvy8znzj">
                        {n}
                      </th>)}
                  </tr>
                  <tr className="bg-gray-100" data-id="element-2180" data-mp="2jm2uyrvk22kv">
                    <th className="border border-black p-1" data-id="element-2181" data-mp="2nmd062biwoyo">Temp</th>
                    <th className="border border-black p-1" data-id="element-2182" data-mp="2nlk2rrygfbt7">Pulse</th>
                    <th className="border border-black p-1" data-id="element-2183" data-mp="1qx5ix6hxt20k">Resp</th>
                    <th className="border border-black p-1" data-id="element-2184" data-mp="1vuxfwexfvebm">BP</th>
                    <th className="border border-black p-1" data-id="element-2185" data-mp="1vt2yvbpag6ct">SpO2</th>
                    <th className="border border-black p-1" data-id="element-2186" data-mp="1zvzagigev279">Weight (Kg)</th>
                    <th className="border border-black p-1" data-id="element-2187" data-mp="24nr0mrvnn5wv">Height</th>
                    <th className="border border-black p-1" data-id="element-2188" data-mp="185kywsvve1yr">BMI</th>
                    <th className="border border-black p-1" data-id="element-2189" data-mp="185cnz6jb56q7">Pain Score</th>
                    <th className="border border-black p-1" data-id="element-2190" data-mp="1c5vhd50umkui">AVPU</th>
                  </tr>
                </thead>
                <tbody data-id="element-2191" data-mp="t3315hwparl2">
                  <tr data-id="element-2192" data-mp="t08j1x15tiut">
                    <td className="border border-black p-2 font-medium" data-id="element-2193" data-mp="o3qi788ya4nr">
                      {selectedEncounter.vitals?.temp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2194" data-mp="12tpd9257kzz0">
                      {selectedEncounter.vitals?.pulse || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2195" data-mp="1bs9pbkg9kbe9">
                      {selectedEncounter.vitals?.resp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2196" data-mp="16wc696dm8qxs">
                      {selectedEncounter.vitals?.bp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2197" data-mp="1gtgvxg6o74jc">
                      {selectedEncounter.vitals?.sp02 || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2198" data-mp="3ijse33i9f1hu">
                      {selectedEncounter.vitals?.weight || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2199" data-mp="1y29okg6swz3s">
                      {selectedEncounter.vitals?.height || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2200" data-mp="1ocgcle2qyhz0">
                      {selectedEncounter.vitals?.bmi || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2201" data-mp="1t8f1d2rzm1zy">
                      {selectedEncounter.vitals?.painScore || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2202" data-mp="1je56nefkorm2">
                      {selectedEncounter.vitals?.avpu || ''}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ═══ STAT DOSES ADMINISTERED BY NURSING ═══ */}
            {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="border-2 border-black mb-6" data-id="element-2203" data-mp="34p57klweuttk">
                  <h3 className="font-bold uppercase bg-yellow-100 p-2 border-b border-black text-center text-sm" data-id="element-2204" data-mp="33915jgx8ftv9">
                    ⚡ Stat Doses Administered by Nursing
                  </h3>
                  <table className="w-full border-collapse text-sm" data-id="element-2205" data-mp="2t9s7abvc3t33">
                    <thead data-id="element-2206" data-mp="2obibq05p6qgy">
                      <tr className="bg-gray-100" data-id="element-2207" data-mp="2oca8heb16u8t">
                        <th className="border border-black p-1.5 w-10" data-id="element-2208" data-mp="2odl6sylpj9sj">No.</th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2209" data-mp="3lypk3zgs0xog">
                          Drug Name
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2210" data-mp="3c9636zcmjdw8">
                          Dose
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2211" data-mp="379l4mq1d97k1">
                          Route
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2212" data-mp="8mffurj5r9uu">
                          Time Given
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2213" data-mp="c31x0ir2zlm6">
                          Nurse
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2214" data-mp="l2f6vcj4ac7f">
                      {selectedEncounter.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-2215" data-mp="q2kumhm9nek8" data-mpi={idx}>
                          <td className="border border-black p-1.5 text-center" data-id="element-2216" data-mp="q3cq7sfv5cai">
                            {idx + 1}
                          </td>
                          <td className="border border-black p-1.5 font-medium" data-id="element-2217" data-mp="z27swujp8492">
                            {dose.drugName}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-2218" data-mp="13vj417vjn76s">
                            {dose.dose}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-2219" data-mp="1ds5o3ug1pu7r">
                            {dose.route}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-2220" data-mp="1ir8gofeua7yu">
                            {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                          </td>
                          <td className="border border-black p-1.5 italic text-xs" data-id="element-2221" data-mp="2d23jhzwv393a">
                            {dose.nurseName || ''}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}

            {/* ═══ CLINICAL NOTES ═══ */}
            <div className="space-y-5 mb-6 text-sm" data-id="element-2222" data-mp="1gdnvw9dbb9st">
              {/* PRESENTING COMPLAINT */}
              <div className="border-2 border-black" data-id="element-2223" data-mp="1u52dqcvhh4ya">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2224" data-mp="1pd11us289g8s">
                  Presenting Complaint
                </h3>
                <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.presentingComplaint} onChange={e => setNotes({
                ...notes,
                presentingComplaint: e.target.value
              })} data-id="element-2225" data-mp="11ldlh42ldnkr" />
              </div>

              {/* HISTORY OF PRESENTING COMPLAINT */}
              <div className="border-2 border-black" data-id="element-2226" data-mp="34awxeref9tuk">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2227" data-mp="3rzg81dky2ipt">
                  History of Presenting Complaint
                </h3>
                <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.historyOfPresentingComplaint} onChange={e => setNotes({
                ...notes,
                historyOfPresentingComplaint: e.target.value
              })} data-id="element-2228" data-mp="3w0i1fds6k0jv" />
              </div>

              {/* PAST MEDICAL HISTORY */}
              <div className="border-2 border-black" data-id="element-2229" data-mp="1tx67umd0fhvq">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2230" data-mp="1yoobji6f3wke">
                  Past Medical History / General Co-Morbidities
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2231" data-mp="1jxxk4b8qmzht">
                  <tbody data-id="element-2232" data-mp="1ozedzmst1low">
                    {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => {
                    const checked = notes.pastMedicalHistory?.includes(condition) || false;
                    return <tr key={condition} className="border-b border-gray-300" data-id="element-2233" data-mp="sayofqpp3cbs">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2234" data-mp="opdcb76ctihs">
                            {condition}
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-2235" data-mp="yokqtxotrj6h">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-2236" data-mp="tprlisjoyyhe">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={checked} onChange={e => {
                            const current = notes.pastMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                pastMedicalHistory: [...current, condition]
                              });
                            } else {
                              setNotes({
                                ...notes,
                                pastMedicalHistory: current.filter(c => c !== condition)
                              });
                            }
                          }} data-id="element-2237" data-mp="606udmddwux1" />
                              {checked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-2238" data-mp="2zngz84mfca5">
                            <input type="text" placeholder="Notes (optional)" disabled={!checked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.pastMedicalHistoryNotes?.[condition] || ''} onChange={e => setNotes({
                          ...notes,
                          pastMedicalHistoryNotes: {
                            ...(notes.pastMedicalHistoryNotes || {}),
                            [condition]: e.target.value
                          }
                        })} data-id="element-2239" data-mp="30gerjllqrg5" />
                          </td>
                        </tr>;
                  })}
                  </tbody>
                </table>
              </div>

              {/* DRUG HISTORY */}
              <div className="border-2 border-black" data-id="element-2240" data-mp="3vof02gv0bda7">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2241" data-mp="7ioy0qygpli7">
                  Drug History
                </h3>
                <textarea className="w-full p-3 min-h-[60px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.drugHistory} onChange={e => setNotes({
                ...notes,
                drugHistory: e.target.value
              })} data-id="element-2242" data-mp="cg750bsx5qnk" />
                <div className="p-3 border-t border-gray-300 bg-gray-50" data-id="element-2243" data-mp="3pp3jdaz28xqm">
                  <span className="font-semibold" data-id="element-2244" data-mp="3aznxa8ah5gh9">Any known allergy:</span>{' '}
                  <span className="text-red-600 font-medium" data-id="element-2245" data-mp="3fy8mlco6ufqx">
                    {patient.allergies}
                  </span>
                </div>
              </div>

              {/* FAMILY MEDICAL HISTORY */}
              <div className="border-2 border-black" data-id="element-2246" data-mp="35zt581eyqkum">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2247" data-mp="35z9m4hdktqf0">
                  Family Medical History
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2248" data-mp="2idfohabodlwz">
                  <tbody data-id="element-2249" data-mp="2wcfgmryecixw">
                    {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => {
                    const checked = notes.familyMedicalHistory?.includes(condition) || false;
                    return <tr key={condition} className="border-b border-gray-300" data-id="element-2250" data-mp="3b1boop0gdvyi">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2251" data-mp="3p1mgbu9l7ekq">
                            {condition}
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-2252" data-mp="2hpelx2fbgv2b">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-2253" data-mp="2lpp2ta4nd5vy">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={checked} onChange={e => {
                            const current = notes.familyMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: [...current, condition]
                              });
                            } else {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: current.filter(c => c !== condition)
                              });
                            }
                          }} data-id="element-2254" data-mp="2qpjql4t4lfcs" />
                              {checked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-2255" data-mp="1qiemudiiw0u4">
                            <input type="text" placeholder="Notes (e.g. relative, severity)" disabled={!checked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.familyMedicalHistoryNotes?.[condition] || ''} onChange={e => setNotes({
                          ...notes,
                          familyMedicalHistoryNotes: {
                            ...(notes.familyMedicalHistoryNotes || {}),
                            [condition]: e.target.value
                          }
                        })} data-id="element-2256" data-mp="1li0jh8rv9f2f" />
                          </td>
                        </tr>;
                  })}
                    {(() => {
                    const otherChecked = notes.familyMedicalHistory?.includes('OTHER') || false;
                    return <tr className="border-b border-gray-300" data-id="element-2257" data-mp="3ckd936zg34w4">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2258" data-mp="3ly577sdefc1i">
                            OTHER
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-2259" data-mp="3hwjq1utu0bli">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-2260" data-mp="3vnovjdjvry3r">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={otherChecked} onChange={e => {
                            const current = notes.familyMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: [...current, 'OTHER']
                              });
                            } else {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: current.filter(c => c !== 'OTHER')
                              });
                            }
                          }} data-id="element-2261" data-mp="3qpncgsmgfysa" />
                              {otherChecked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-2262" data-mp="2h86fy2fkopdg">
                            <input type="text" placeholder="Specify (e.g. condition, relative)" disabled={!otherChecked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.familyMedicalHistoryOther || ''} onChange={e => setNotes({
                          ...notes,
                          familyMedicalHistoryOther: e.target.value
                        })} data-id="element-2263" data-mp="2h7df1i3aou38" />
                          </td>
                        </tr>;
                  })()}
                  </tbody>
                </table>
              </div>

              {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
              <div className="border-2 border-black" data-id="element-2264" data-mp="ipjoiywtbwqa">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2265" data-mp="msga8yhng8yg">
                  Obstetrics and Gynaecology History
                </h3>
                <textarea className="w-full p-3 min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.obgynHistory} onChange={e => setNotes({
                ...notes,
                obgynHistory: e.target.value
              })} data-id="element-2266" data-mp="rooli0sq7i0c" />
              </div>

              {/* SOCIAL HISTORY */}
              <div className="border-2 border-black" data-id="element-2267" data-mp="37hdgabd5meb0">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2268" data-mp="37jzrqm361dyi">
                  Social History
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2269" data-mp="3ghjtrx8urea6">
                  <tbody data-id="element-2270" data-mp="3bj8jjlenb1rf">
                    <tr className="border-b border-gray-300" data-id="element-2271" data-mp="3bktbd39ks6ll">
                      <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2272" data-mp="1m4k6rj257hsv">
                        Alcohol
                      </td>
                      <td className="p-2" data-id="element-2273" data-mp="1h70qyepsqwyt">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.alcohol || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          alcohol: e.target.value
                        }
                      })} data-id="element-2274" data-mp="3st2o0s3m9mun" />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300" data-id="element-2275" data-mp="352ymqrdwhz6t">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2276" data-mp="355kv2t8m8iq3">
                        Smoking
                      </td>
                      <td className="p-2" data-id="element-2277" data-mp="13cn30zmnxu67">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.smoking || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          smoking: e.target.value
                        }
                      })} data-id="element-2278" data-mp="13bc2d8oil2jb" />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300" data-id="element-2279" data-mp="3hzv0ungmmvxy">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2280" data-mp="3d4fu0fh1e0b1">
                        Occupation
                      </td>
                      <td className="p-2" data-id="element-2281" data-mp="342ev7e65n679">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.occupation || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          occupation: e.target.value
                        }
                      })} data-id="element-2282" data-mp="343hlbjvnl4m4" />
                      </td>
                    </tr>
                    <tr data-id="element-2283" data-mp="19t9o8crrm8i0">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2284" data-mp="1elaxdptmzj3t">
                        Mental Status
                      </td>
                      <td className="p-2" data-id="element-2285" data-mp="h4vsk3o5fo9m">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.mentalStatus || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          mentalStatus: e.target.value
                        }
                      })} data-id="element-2286" data-mp="h42rnjbvfsze" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* REVIEW OF SYSTEMS */}
              <div className="border-2 border-black" data-id="element-2287" data-mp="49pk944grql">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2288" data-mp="6m5kgqbks10">
                  Review of Systems
                </h3>
                <div className="p-3 space-y-2" data-id="element-2289" data-mp="1bimzsb5yd8o5">
                  {[{
                  key: 'rs',
                  label: 'R/S:'
                }, {
                  key: 'cvs',
                  label: 'CVS:'
                }, {
                  key: 'cns',
                  label: 'CNS:'
                }, {
                  key: 'git',
                  label: 'GIT:'
                }, {
                  key: 'gut',
                  label: 'GUT:'
                }, {
                  key: 'mss',
                  label: 'MSS:'
                }, {
                  key: 'is',
                  label: 'IS:'
                }].map(system => <div key={system.key} className="flex items-center" data-id="element-2290" data-mp="3w2lilt4mv7vx">
                      <span className="font-semibold w-16" data-id="element-2291" data-mp="4qk1wow5cbqf">{system.label}</span>
                      <input type="text" className="flex-1 p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.reviewOfSystems?.[system.key as keyof typeof notes.reviewOfSystems] || ''} onChange={e => setNotes({
                    ...notes,
                    reviewOfSystems: {
                      ...notes.reviewOfSystems,
                      [system.key]: e.target.value
                    }
                  })} data-id="element-2292" data-mp="20t5a8khvdxku" />
                    </div>)}
                </div>
              </div>

              {/* ON EXAMINATION */}
              <div className="border-2 border-black" data-id="element-2293" data-mp="ubrvy0j6d30o">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2294" data-mp="35zwtjrvrx4fh">
                  On Examination
                </h3>
                <div className="p-4 space-y-3" data-id="element-2295" data-mp="3a2b9a345bo2j">
                  {[{
                  key: 'gc' as const,
                  label: 'G/C',
                  helper: 'Stable / Ill Looking / Unconscious'
                }, {
                  key: 'febrile' as const,
                  label: 'Temperature',
                  helper: 'Febrile / Afebrile / Lethargic'
                }, {
                  key: 'pjc' as const,
                  label: 'P | J | C',
                  helper: 'Pallor / Jaundice / Cyanosis'
                }, {
                  key: 's1s2' as const,
                  label: 'S1  S2',
                  helper: 'Heart sounds'
                }, {
                  key: 'rs' as const,
                  label: 'R/S',
                  helper: 'Respiratory system'
                }, {
                  key: 'pa' as const,
                  label: 'P/A',
                  helper: 'Per abdomen'
                }].map(field => <div key={field.key} className="grid grid-cols-[7rem_1fr] gap-3 items-start" data-id="element-2296" data-mp="26vvh3rrmdape">
                      <div className="pt-2" data-id="element-2297" data-mp="2fuh4q1by7fx1">
                        <p className="font-bold text-sm text-gray-900" data-id="element-2298" data-mp="2ft5txhk357wf">
                          {field.label}
                        </p>
                        <p className="text-xs text-gray-500 leading-tight" data-id="element-2299" data-mp="2pswmveszzeue">
                          {field.helper}
                        </p>
                      </div>
                      <input type="text" value={notes.onExaminationDetails?.[field.key] || ''} onChange={e => setNotes({
                    ...notes,
                    onExaminationDetails: {
                      ...(notes.onExaminationDetails || {
                        gc: '',
                        febrile: '',
                        pjc: '',
                        s1s2: '',
                        rs: '',
                        pa: ''
                      }),
                      [field.key]: e.target.value
                    }
                  })} placeholder={`Enter ${field.label} findings`} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none" data-id="element-2300" data-mp="qi58c7pk1y9f" />
                    </div>)}

                  {/* Additional notes (free text) */}
                  <div className="pt-3 border-t border-gray-200" data-id="element-2301" data-mp="23r1noch5509h">
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" data-id="element-2302" data-mp="28pv1y7494vap">
                      Additional Notes
                    </label>
                    <textarea className="w-full p-2 min-h-[80px] text-sm border-2 border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="Any other examination findings..." value={notes.onExamination} onChange={e => setNotes({
                    ...notes,
                    onExamination: e.target.value
                  })} data-id="element-2303" data-mp="1zowybpxq6psn" />
                  </div>
                </div>
              </div>

              {/* SUMMARY OF SIGNIFICANT FINDINGS */}
              <div className="border-2 border-black" data-id="element-2304" data-mp="1723s4s1kgyb9">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2305" data-mp="nfircyq057t0">
                  Summary of Significant Findings
                </h3>
                <div className="p-3 space-y-2" data-id="element-2306" data-mp="xcdmr8qzas32">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <div key={i} className="flex items-start" data-id="element-2307" data-mp="sd10oq7xlp1j">
                      <span className="font-medium mr-2 mt-1" data-id="element-2308" data-mp="sfn7uoqwu2j8">{i + 1}.</span>
                      <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.significantFindings?.[i] || ''} onChange={e => {
                    const current = notes.significantFindings || Array(8).fill('');
                    const updated = [...current];
                    updated[i] = e.target.value;
                    setNotes({
                      ...notes,
                      significantFindings: updated
                    });
                  }} data-id="element-2309" data-mp="16bqyy96hlurh" />
                    </div>)}
                </div>
              </div>

              {/* WORKING DIAGNOSIS */}
              <div className="border-2 border-black" data-id="element-2310" data-mp="3tacrppdzrb3z">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2311" data-mp="3t91lzk0zjta8">
                  Working Diagnosis
                </h3>
                <div className="p-3 space-y-2" data-id="element-2312" data-mp="30knqbaxdd91u">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => <div key={i} className="flex items-start" data-id="element-2313" data-mp="35j7cncfvkmuq">
                      <span className="font-medium mr-2 mt-1" data-id="element-2314" data-mp="39lvo7rvz47ve">{i + 1}.</span>
                      <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.workingDiagnosis?.[i] || ''} onChange={e => {
                    const current = notes.workingDiagnosis || Array(10).fill('');
                    const updated = [...current];
                    updated[i] = e.target.value;
                    setNotes({
                      ...notes,
                      workingDiagnosis: updated
                    });
                  }} data-id="element-2315" data-mp="39jr7uagw1ge8" />
                    </div>)}
                </div>
              </div>

              {/* INVESTIGATIONS TO BE DONE */}
              <div className="border-2 border-black" data-id="element-2316" data-mp="1hlpviur9cje3">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2317" data-mp="1hoca3eckfz5t">
                  Investigations To Be Done
                </h3>
                <div className="p-4" data-id="element-2318" data-mp="3c00isys7f5hr">
                  {/* LABORATORY TESTS */}
                  <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-2319" data-mp="372h5c13bywqv">
                    Laboratory Tests
                  </h4>
                  <table className="w-full border-collapse text-sm mb-4" data-id="element-2320" data-mp="3uwr8378r8o1r">
                    <tbody data-id="element-2321" data-mp="3uyvkkdkpyvdn">
                      {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATININE / U&E', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['HORMONAL PROFILE', 'ANTI-MÜLLERIAN HORMONE'], ['RPR', 'HIV'], ['SPERM ANALYSIS', 'TESTOSTERONE'], ['DOA 6', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-2322" data-mp="2th38ccwhqpaf" data-mpi={idx}>
                          {pair.map((test, ci) => {
                        if (!test) {
                          return <Fragment key={`empty-${ci}`} data-id="element-2323" data-mp="2gzime16b21zg">
                                  <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-2324" data-mp="2uvnonbr8ulzk"></td>
                                  <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-2325" data-mp="2pz5q4tmibjvo"></td>
                                </Fragment>;
                        }
                        const isOrdered = selectedEncounter.labRequests.some(lab => lab.tests.some(t => t.toLowerCase().includes(test.toLowerCase()) || test.toLowerCase().includes(t.toLowerCase())));
                        return <Fragment key={test} data-id="element-2326" data-mp="y0jr5ch2qsxs" data-mpi={ci}>
                                <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-2327" data-mp="12xagyriol2we">
                                  {test}
                                </td>
                                <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-2328" data-mp="1cvzphtootazz">
                                  {isOrdered ? <span className="text-green-700 font-bold" data-id="element-2329" data-mp="1gvs16f9dnx9k">
                                      ✓
                                    </span> : ''}
                                </td>
                              </Fragment>;
                      })}
                        </tr>)}
                    </tbody>
                  </table>

                  {/* RADIOLOGY SCANS */}
                  <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-2330" data-mp="3g3qmtcesis0k">
                    Radiology Scans
                  </h4>
                  <table className="w-full border-collapse text-sm mb-4" data-id="element-2331" data-mp="3b5f9ust781e4">
                    <tbody data-id="element-2332" data-mp="367dt4ej8we5t">
                      {[['PELVIC SCAN', 'ANOMALY SCAN'], ['OBS SCAN', 'CXR (CHEST X-RAY)'], ['HSG (HYSTEROSALPINGOGRAPHY)', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-2333" data-mp="3u39j97y8oex1" data-mpi={idx}>
                          {pair.map((scan, ci) => {
                        if (!scan) {
                          return <Fragment key={`empty-r-${ci}`} data-id="element-2334" data-mp="qfznca10iyua">
                                  <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-2335" data-mp="lohopsmgpach"></td>
                                  <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-2336" data-mp="1nd3eny5xx4sg"></td>
                                </Fragment>;
                        }
                        const isOrdered = selectedEncounter.radiologyRequests.some(r => {
                          const a = r.scanType.toLowerCase();
                          const b = scan.toLowerCase();
                          return a.includes(b) || b.includes(a) || b.includes('cxr') && a.includes('cxr') || b.includes('hsg') && a.includes('hsg');
                        });
                        return <Fragment key={scan} data-id="element-2337" data-mp="2p38irjvhe9ye" data-mpi={ci}>
                                <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-2338" data-mp="2k3nlrezvgbob">
                                  {scan}
                                </td>
                                <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-2339" data-mp="33n32i7hnzqhp">
                                  {isOrdered ? <span className="text-green-700 font-bold" data-id="element-2340" data-mp="2unfw17a2vuhs">
                                      ✓
                                    </span> : ''}
                                </td>
                              </Fragment>;
                      })}
                        </tr>)}
                    </tbody>
                  </table>

                  {/* ─── PENDING INVESTIGATIONS — RECALL ─── */}
                  {(() => {
                  const pendingLabs = selectedEncounter.labRequests.filter(l => l.status === 'Pending');
                  const pendingRads = selectedEncounter.radiologyRequests.filter(r => r.status === 'Pending');
                  const hasAny = pendingLabs.some(l => l.tests.length > 0 || l.otherTests.trim().length) || pendingRads.length > 0;
                  if (!hasAny) return null;
                  return <div className="mb-4 border border-amber-300 bg-amber-50 rounded-md p-3" data-id="element-2341" data-mp="vgrdxeuaf04t">
                        <h4 className="font-bold text-xs uppercase text-amber-800 mb-2" data-id="element-2342" data-mp="qizdtxjho7pj">
                          Pending Investigations (Recall to make changes)
                        </h4>
                        <ul className="space-y-1.5 text-sm" data-id="element-2343" data-mp="hh9iwhqeeqn0">
                          {pendingLabs.flatMap(lab => lab.tests.map(testName => <li key={`${lab.id}-${testName}`} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-2344" data-mp="326ywysja28en">
                                <span data-id="element-2345" data-mp="1qqnwdwog3ui0">
                                  <span className="font-medium" data-id="element-2346" data-mp="1qpl71t6ru047">
                                    {testName}
                                  </span>{' '}
                                  <span className="text-xs text-gray-500" data-id="element-2347" data-mp="20gfv933kv9sa">
                                    · Laboratory ·{' '}
                                    {new Date(lab.requestedAt).toLocaleTimeString()}
                                  </span>
                                </span>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallLabTest(lab.id, testName)} data-id="element-2348" data-mp="2effhy57j5elt">
                                  <RotateCcw className="w-3 h-3 mr-1" data-id="element-2349" data-mp="3uvkyz2rsrjix" /> Recall
                                </Button>
                              </li>))}
                          {pendingLabs.filter(l => l.otherTests.trim().length > 0).map(lab => <li key={`${lab.id}-other`} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-2350" data-mp="2x9nn3o7xacfy">
                                <span data-id="element-2351" data-mp="2jfnzikmc1h7g">
                                  <span className="font-medium" data-id="element-2352" data-mp="2jela6h4nrmtn">
                                    {lab.otherTests}
                                  </span>{' '}
                                  <span className="text-xs text-gray-500" data-id="element-2353" data-mp="tz24ycuw98lf">
                                    · Other lab test
                                  </span>
                                </span>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallLabTest(lab.id, lab.otherTests, true)} data-id="element-2354" data-mp="kzngfiyaf83k">
                                  <RotateCcw className="w-3 h-3 mr-1" data-id="element-2355" data-mp="3of2y96cwqlz9" /> Recall
                                </Button>
                              </li>)}
                          {pendingRads.map(rad => <li key={rad.id} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-2356" data-mp="1yxfii1aqoahs">
                              <span data-id="element-2357" data-mp="1l72da6p8f9w5">
                                <span className="font-medium" data-id="element-2358" data-mp="1l5r7608bdq1v">
                                  {rad.scanType}
                                </span>{' '}
                                <span className="text-xs text-gray-500" data-id="element-2359" data-mp="1b8mls2malym4">
                                  · Radiology ·{' '}
                                  {new Date(rad.requestedAt).toLocaleTimeString()}
                                </span>
                              </span>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallRadiology(rad.id)} data-id="element-2360" data-mp="30n170cy3r3cv">
                                <RotateCcw className="w-3 h-3 mr-1" data-id="element-2361" data-mp="s3zvxxcbpznw" /> Recall
                              </Button>
                            </li>)}
                        </ul>
                        <p className="text-xs text-amber-700 mt-2 italic" data-id="element-2362" data-mp="3vkq9cxa8jezm">
                          Recall removes the request before the lab or radiology
                          department processes it. Once a test is completed, it
                          becomes part of the permanent record.
                        </p>
                      </div>;
                })()}

                  <div className="flex gap-3 mb-4" data-id="element-2363" data-mp="1jv7e8nvtppej">
                    <Button variant="outline" onClick={() => setShowLabModal(true)} className="flex-1" data-id="element-2364" data-mp="1jue7hmwv6zwe">
                      <FlaskConical className="w-4 h-4 mr-2" data-id="element-2365" data-mp="10ii17vvbnrso" /> Request
                      Laboratory Test
                    </Button>
                    <Button variant="outline" onClick={() => setShowRadModal(true)} className="flex-1 border-purple-600 text-purple-600 hover:bg-purple-50" data-id="element-2366" data-mp="vgsombsgb62h">
                      <Scan className="w-4 h-4 mr-2" data-id="element-2367" data-mp="1j7ewa1yjmoby" /> Request Radiology Scan
                    </Button>
                  </div>

                  <div className="border-t border-gray-300 pt-2" data-id="element-2368" data-mp="1e8lg21rvgj83">
                    <span className="font-semibold" data-id="element-2369" data-mp="1s963jb8qf37x">Other investigations:</span>{' '}
                    {selectedEncounter.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                  </div>

                  {/* Investigation Results — View File per test */}
                  {(selectedEncounter.labRequests.some(l => l.status === 'Completed') || selectedEncounter.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-2370" data-mp="364nv5v2kfq9l">
                      <h4 className="font-bold mb-3 uppercase text-center" data-id="element-2371" data-mp="3b2z6ka8ie8tx">
                        Investigation Results
                      </h4>
                      <table className="w-full border-collapse text-sm" data-id="element-2372" data-mp="2w92y8bcctg6p">
                        <thead data-id="element-2373" data-mp="3174hoxdozhip">
                          <tr className="bg-gray-100" data-id="element-2374" data-mp="2n61ughayo167">
                            <th className="border border-black p-1.5 text-left" data-id="element-2375" data-mp="2n7mmo09sz80w">
                              Test / Scan
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2376" data-mp="leyjfqx5skl4">
                              Type
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2377" data-mp="qd9ta1ngev3c">
                              Performed By
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2378" data-mp="jhwirmc6mxs6">
                              Completed
                            </th>
                            <th className="border border-black p-1.5 text-center w-32" data-id="element-2379" data-mp="5nvujmiuwt62">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-2380" data-mp="qvmj62g5gcb">
                          {selectedEncounter.labRequests.filter(l => l.status === 'Completed').flatMap(lab => lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).map(testName => <tr key={`${lab.id}-${testName}`} data-id="element-2381" data-mp="35orcnecn4g3j">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-2382" data-mp="35p11gyly8h7n">
                                      {testName}
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-2383" data-mp="3ffcinmun8mwh">
                                      Laboratory
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-2384" data-mp="3pc8rjtogfb1k">
                                      —
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2385" data-mp="3kfrxkiukq2gk">
                                      {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center" data-id="element-2386" data-mp="1z2p2cf5jvkyj">
                                      <div className="flex justify-center gap-2" data-id="element-2387" data-mp="2zan9ikvb9lz">
                                        <Button size="sm" variant="outline" onClick={() => setViewingTest({
                              kind: 'lab',
                              labId: lab.id,
                              testName
                            })} data-id="element-2388" data-mp="7wsth17i3aqa">
                                          <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2389" data-mp="10jn5rau5z3so" />{' '}
                                          View File
                                        </Button>
                                        <button onClick={() => setRemoveTestModal({
                              isOpen: true,
                              labId: lab.id,
                              testName,
                              reason: ''
                            })} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors print:hidden" title="Remove duplicate test" data-id="element-2390" data-mp="19mo9x5zgyktp">
                                          <Trash2 className="w-4 h-4" data-id="element-2391" data-mp="33ziekcq8zwg1" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>))}
                          {selectedEncounter.labRequests.filter(l => l.status === 'Completed' && l.otherTests && !(l.removedTestNames || []).includes(l.otherTests)).map(lab => <tr key={`${lab.id}-other`} data-id="element-2392" data-mp="3v0pbd487ttjh">
                                <td className="border border-black p-1.5 font-medium" data-id="element-2393" data-mp="2wcztr676aft">
                                  {lab.otherTests}{' '}
                                  <span className="text-xs text-gray-500" data-id="element-2394" data-mp="3l9ue74tfl9j9">
                                    (Other)
                                  </span>
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2395" data-mp="hs2ojx2rp1o8">
                                  Laboratory
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2396" data-mp="vy4czzlr0awf">—</td>
                                <td className="border border-black p-1.5 text-xs" data-id="element-2397" data-mp="rxkfgux9j59r">
                                  {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                </td>
                                <td className="border border-black p-1.5 text-center" data-id="element-2398" data-mp="d01vs6zfzi96">
                                  <div className="flex justify-center gap-2" data-id="element-2399" data-mp="d1v03fml1qe0">
                                    <Button size="sm" variant="outline" onClick={() => setViewingTest({
                              kind: 'lab',
                              labId: lab.id,
                              testName: lab.otherTests
                            })} data-id="element-2400" data-mp="909gzcjggfvd">
                                      <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2401" data-mp="25ftatw5hppoh" /> View
                                      File
                                    </Button>
                                    <button onClick={() => setRemoveTestModal({
                              isOpen: true,
                              labId: lab.id,
                              testName: lab.otherTests,
                              reason: ''
                            })} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors print:hidden" title="Remove duplicate test" data-id="element-2402" data-mp="2fequolrz0v15">
                                      <Trash2 className="w-4 h-4" data-id="element-2403" data-mp="wgm695gwosv0" />
                                    </button>
                                  </div>
                                </td>
                              </tr>)}
                          {selectedEncounter.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <tr key={rad.id} data-id="element-2404" data-mp="di055qle31r3">
                                <td className="border border-black p-1.5 font-medium" data-id="element-2405" data-mp="8qi3t1fgez5l">
                                  {rad.scanType}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2406" data-mp="3payra4p4e2m">
                                  Radiology
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2407" data-mp="3pxkai3losja1">
                                  {rad.radiographerName || '—'}
                                </td>
                                <td className="border border-black p-1.5 text-xs" data-id="element-2408" data-mp="1dolktbj1bfgy">
                                  {rad.completedAt ? new Date(rad.completedAt).toLocaleString() : ''}
                                </td>
                                <td className="border border-black p-1.5 text-center" data-id="element-2409" data-mp="18oz4uubtnl4g">
                                  <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'rad',
                            radId: rad.id
                          })} data-id="element-2410" data-mp="uxue6hqfm369">
                                    <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2411" data-mp="bzrtgf4x2se9" /> View
                                    File
                                  </Button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </div>
              </div>

              {/* DRUG TREATMENT / PRESCRIPTION */}
              <div className="border-2 border-black" data-id="element-2412" data-mp="1dlfxqpvmr25">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2413" data-mp="1fz9tw8tlx84">
                  Drug Treatment / Prescription
                  {selectedEncounter.prescriptions.length > 0 && <span className="ml-2 text-xs font-normal text-gray-600 lowercase" data-id="element-2414" data-mp="p3fohw35xtgl">
                      ({selectedEncounter.prescriptions.length} of 20 drugs)
                    </span>}
                </h3>
                <div className="max-h-[420px] overflow-y-auto" data-id="element-2415" data-mp="z2oi2nu4962f">
                  <table className="w-full border-collapse text-sm text-center" data-id="element-2416" data-mp="u2k1yyx4edcq">
                    <thead data-id="element-2417" data-mp="u0fp3rh8u40b">
                      <tr className="bg-gray-100" data-id="element-2418" data-mp="mjd9c0n409c7">
                        <th className="border border-black p-1.5 w-10" data-id="element-2419" data-mp="mityf3vvml7o">No.</th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2420" data-mp="ijctcbkd3q31">
                          Drug Name
                        </th>
                        <th className="border border-black p-1.5" data-id="element-2421" data-mp="dkrt4c5416el">Dose</th>
                        <th className="border border-black p-1.5" data-id="element-2422" data-mp="8jlwi38bwyzs">Route</th>
                        <th className="border border-black p-1.5" data-id="element-2423" data-mp="3lsp56vahyxx">Frequency</th>
                        <th className="border border-black p-1.5" data-id="element-2424" data-mp="3uywyooknt7lz">
                          No. of Days
                        </th>
                        <th className="border border-black p-1.5" data-id="element-2425" data-mp="3qycx98t1zhu1">Signature</th>
                        <th className="border border-black p-1.5 w-16" data-id="element-2426" data-mp="3lzta552q4003">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2427" data-mp="3c0as4u6dioao">
                      {selectedEncounter.prescriptions.length > 0 ? selectedEncounter.prescriptions.map((rx, i) => <tr key={rx.id} className={rx.status === 'Dispensed' ? 'bg-green-50' : rx.status === 'Prescribed' || rx.status === 'Not Dispensed' ? 'bg-amber-50' : ''} data-id="element-2428" data-mp="36vgrugw72ziu" data-mpi={i}>
                              <td className="border border-black p-1.5" data-id="element-2429" data-mp="2t3sfqdly4b1v">
                                {i + 1}
                              </td>
                              <td className="border border-black p-1.5 text-left font-medium" data-id="element-2430" data-mp="2xwd9jf4uqo5e">
                                {rx.drugName}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2431" data-mp="25dgcvh5k54i5">
                                {rx.dose}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2432" data-mp="1seyrfmypfe5e">
                                {rx.route}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2433" data-mp="272ju5fro3bcs">
                                {rx.frequency}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2434" data-mp="1eexq66ej24pe">
                                {rx.days}
                              </td>
                              <td className="border border-black p-1.5 text-xs" data-id="element-2435" data-mp="19hnsk3gzimqh">
                                {rx.status === 'Dispensed' && <span className="text-green-700 font-bold" data-id="element-2436" data-mp="1jek3efucvky7">
                                    ✓ Dispensed
                                  </span>}
                                {(rx.status === 'Prescribed' || rx.status === 'Not Dispensed') && <span className="font-bold text-amber-700" data-id="element-2437" data-mp="32bx3n6e5kro3">
                                    ℞ Prescribed — not in stock
                                  </span>}
                                {rx.status === 'Pending' && <span className="italic text-gray-500" data-id="element-2438" data-mp="2xdc7bi20us4x">
                                    Pending
                                  </span>}
                                {rx.pharmacistName && <div className="text-gray-500 italic mt-0.5" data-id="element-2439" data-mp="3q1rks05i92e5">
                                    {rx.pharmacistName}
                                  </div>}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2440" data-mp="7wehw5o95exh">
                                {rx.status === 'Pending' ? <button onClick={() => handleDeletePrescription(rx.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors" title="Remove this drug" aria-label={`Remove ${rx.drugName}`} data-id="element-2441" data-mp="cp7l2p42of7g">
                                    <Trash2 className="w-4 h-4" data-id="element-2442" data-mp="33up1karb6bkr" />
                                  </button> : <span className="text-gray-300" data-id="element-2443" data-mp="2u5duetnzfgzi">—</span>}
                              </td>
                            </tr>) : Array.from({
                      length: 20
                    }, (_, idx) => idx + 1).map(i => <tr key={i} data-id="element-2444" data-mp="14ddjv6iz2gw7">
                              <td className="border border-black p-3" data-id="element-2445" data-mp="14eewvgjjtdgk">{i}</td>
                              <td className="border border-black p-3" data-id="element-2446" data-mp="19byjp4ubayk4"></td>
                              <td className="border border-black p-3" data-id="element-2447" data-mp="1dcqrdtnjcvea"></td>
                              <td className="border border-black p-3" data-id="element-2448" data-mp="1ibv4uwhhtfaf"></td>
                              <td className="border border-black p-3" data-id="element-2449" data-mp="1i9qp9ha8erub"></td>
                              <td className="border border-black p-3" data-id="element-2450" data-mp="1naog0i9b4u7f"></td>
                              <td className="border border-black p-3" data-id="element-2451" data-mp="1s9979s6l03jq"></td>
                              <td className="border border-black p-3" data-id="element-2452" data-mp="1x199xse1dak0"></td>
                            </tr>)}
                    </tbody>
                  </table>
                </div>
                <div className="p-3 border-t border-gray-300 flex gap-3" data-id="element-2453" data-mp="2627i8mvic48e">
                  <Button variant="outline" onClick={() => setShowRxModal(true)} className="flex-1 text-green-600 border-green-600 hover:bg-green-50" disabled={selectedEncounter.prescriptions.length >= 20} data-id="element-2454" data-mp="2b0s8pukyj9jn">
                    <Pill className="w-4 h-4 mr-2" data-id="element-2455" data-mp="kocc9dhq5w7l" />
                    {selectedEncounter.prescriptions.length >= 20 ? 'Maximum 20 Drugs Reached' : 'Prescribe Medication'}
                  </Button>
                  {selectedEncounter.prescriptions.some(rx => rx.status === 'Pending') && <Button onClick={handleSendToPharmacy} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-id="element-2456" data-mp="ppruyywfjqmt">
                      <Pill className="w-4 h-4 mr-2" data-id="element-2457" data-mp="yowpzv8s91u8" /> Send Prescription to
                      Pharmacy
                    </Button>}
                </div>
              </div>

              {/* FOLLOW UP / REVIEW DATE */}
              <div className="border-2 border-black" data-id="element-2458" data-mp="1fxpimv8wyweo">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2459" data-mp="1fy7pelt7ct0u">
                  Next Follow Up / Review Date (If Any)
                </h3>
                <table className="w-full border-collapse text-sm text-center" data-id="element-2460" data-mp="166uzw0uvmexz">
                  <thead data-id="element-2461" data-mp="1b5xsumxl0upl">
                    <tr className="bg-gray-100" data-id="element-2462" data-mp="1b54w8esc7ll6">
                      <th className="border border-black p-1 w-10" data-id="element-2463" data-mp="x77vd66uot0p">No.</th>
                      <th className="border border-black p-1" data-id="element-2464" data-mp="x9ax2nhd8al9">Date</th>
                      <th className="border border-black p-1 w-10" data-id="element-2465" data-mp="127m4krk6u90b">No.</th>
                      <th className="border border-black p-1" data-id="element-2466" data-mp="n9ts689xu0qc">Date</th>
                      <th className="border border-black p-1 w-10" data-id="element-2467" data-mp="s7li4wr0vcrw">No.</th>
                      <th className="border border-black p-1" data-id="element-2468" data-mp="s9y19d8int6j">Date</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2469" data-mp="2mlwavgaqmr2y">
                    {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-2470" data-mp="2mjiff6c5bcuv">
                        <td className="border border-black p-1 font-medium" data-id="element-2471" data-mp="1acsp7ep1cpme">
                          {row + 1}
                        </td>
                        <td className="border border-black p-1" data-id="element-2472" data-mp="15e7z483hzmbo">
                          {row === 0 ? <input type="date" className="w-full p-1 text-center focus:ring-2 focus:ring-blue-500 focus:outline-none" value={selectedEncounter.reviewDate ? new Date(selectedEncounter.reviewDate).toISOString().split('T')[0] : ''} onChange={e => {
                        if (e.target.value) {
                          updateEncounter(selectedEncounter.id, {
                            reviewDate: new Date(e.target.value).toISOString()
                          });
                        }
                      }} data-id="element-2473" data-mp="10kmz6aicgi9u" /> : ''}
                        </td>
                        <td className="border border-black p-1 font-medium" data-id="element-2474" data-mp="1vnyn72004g33">
                          {row + 6}
                        </td>
                        <td className="border border-black p-2" data-id="element-2475" data-mp="20n1rulc2kkaj"></td>
                        <td className="border border-black p-1 font-medium" data-id="element-2476" data-mp="25ld1ow2d6usr">
                          {row + 11}
                        </td>
                        <td className="border border-black p-2" data-id="element-2477" data-mp="2aimpk7a3uyeh"></td>
                      </tr>)}
                  </tbody>
                </table>
                <div className="border-t border-black p-3" data-id="element-2478" data-mp="3lytkgsjdw031">
                  <h4 className="font-bold uppercase text-sm mb-2" data-id="element-2479" data-mp="184scj0f7mmhr">Notes</h4>
                  <textarea className="w-full p-2 border border-gray-300 rounded text-sm min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="Add any notes related to the follow-up or review (e.g. reason for review, instructions to patient, expected investigations)…" value={reviewDateNotes} onChange={e => setReviewDateNotes(e.target.value)} data-id="element-2480" data-mp="182pepu7tfp2h" />
                </div>
              </div>

              {/* NAME OF MEDICAL PRACTITIONER */}
              <div className="border-2 border-black p-4 mt-6" data-id="element-2481" data-mp="y7dz79ol2dto">
                <p className="font-bold text-sm" data-id="element-2482" data-mp="u595yc21rj7k">
                  Name of the Medical Practitioner:{' '}
                  <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-2483" data-mp="29l8azegdaz3l">
                    {selectedEncounter.clinicalNote?.clinicianName || currentUser?.fullName || ''}
                  </span>
                </p>
              </div>
            </div>
          </fieldset>

          {/* VERSION HISTORY VIEWER */}
          {selectedEncounter.clinicalNote?.editHistory && selectedEncounter.clinicalNote.editHistory.length > 0 && <div className="mt-8 border-t-2 border-dashed border-gray-300 pt-6 print:hidden" data-id="element-2484" data-mp="285ngqxd4l75l">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center" data-id="element-2485" data-mp="2vv837q8hmmjm">
                  <History className="w-5 h-5 mr-2 text-gray-500" data-id="element-2486" data-mp="30tjd20ys8x1u" />
                  Version History
                </h3>
                <div className="space-y-4" data-id="element-2487" data-mp="2m3luq1njb1kk">
                  {selectedEncounter.clinicalNote.editHistory.map((edit, idx) => <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-2488" data-mp="3etawih82j5n8" data-mpi={idx}>
                        <div className="flex justify-between items-start mb-2" data-id="element-2489" data-mp="3jlu8xav0ruqg">
                          <div data-id="element-2490" data-mp="3tf7yv3t2rd18">
                            <p className="font-semibold text-gray-900" data-id="element-2491" data-mp="3tddky9g80l6n">
                              Edited by {edit.editedByName}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-2492" data-mp="3kdi8m1axik51">
                              {new Date(edit.editedAt).toLocaleString()}
                            </p>
                          </div>
                          <Badge variant="warning" data-id="element-2493" data-mp="3aigh4yt6l5za">Version {idx + 1}</Badge>
                        </div>
                        <div className="bg-white border border-gray-100 p-3 rounded text-sm text-gray-700 mt-2" data-id="element-2494" data-mp="35oue7yctkggo">
                          <span className="font-semibold text-gray-900" data-id="element-2495" data-mp="30ozldp9hhgsz">
                            Reason for change:
                          </span>{' '}
                          {edit.reason}
                        </div>
                        <details className="mt-3" data-id="element-2496" data-mp="2mrkx4nuyvf2l">
                          <summary className="text-sm text-blue-600 cursor-pointer hover:underline font-medium" data-id="element-2497" data-mp="2mrt5q3k23y7z">
                            View previous note content
                          </summary>
                          <div className="mt-2 p-3 bg-white border border-gray-200 rounded text-xs text-gray-600 max-h-60 overflow-y-auto font-mono whitespace-pre-wrap" data-id="element-2498" data-mp="176ilm1wzuanw">
                            {JSON.stringify(edit.previousNote, null, 2)}
                          </div>
                        </details>
                      </div>)}
                </div>
              </div>}
        </div>

        {/* ═══ MODALS ═══ */}
        {removeTestModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2499" data-mp="shcp4lpjiyjp">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-2500" data-mp="xc0fyr80o51g">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center text-red-600" data-id="element-2501" data-mp="s177btpieut">
                <Trash2 className="w-5 h-5 mr-2" data-id="element-2502" data-mp="4s1xbtq64ehp" /> Remove Duplicate Test
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-2503" data-mp="3hv5pb0gct0ne">
                Why are you removing "{removeTestModal.testName}"? This action
                will be logged in the audit trail.
              </p>
              <div className="mb-4" data-id="element-2504" data-mp="3rlrxwapnkqwl">
                <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none" rows={3} value={removeTestModal.reason} onChange={e => setRemoveTestModal({
              ...removeTestModal,
              reason: e.target.value
            })} placeholder="e.g., Duplicate entry, entered by mistake..." data-id="element-2505" data-mp="3mu0dyj105jff" />
              </div>
              <div className="flex justify-end space-x-3" data-id="element-2506" data-mp="2g5k2qucjwhuy">
                <Button variant="outline" onClick={() => setRemoveTestModal({
              isOpen: false,
              labId: '',
              testName: '',
              reason: ''
            })} data-id="element-2507" data-mp="2b6z0yqhnhq4e">
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleRemoveTestConfirm} disabled={!removeTestModal.reason.trim()} data-id="element-2508" data-mp="lx6e422qzle1">
                  Confirm Removal
                </Button>
              </div>
            </div>
          </div>}

        {/* Lab Modal */}
        {showLabModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-2509" data-mp="3u6zozl8k4ub8">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" data-id="element-2510" data-mp="30f0o99w00hw">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2511" data-mp="1sbp6sr6p0p37">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2512" data-mp="1vvfyryml6uu6">
                  Request Laboratory Tests
                </h3>
                <button onClick={() => setShowLabModal(false)} data-id="element-2513" data-mp="1qw3cbezmnps4">
                  <X className="w-5 h-5 text-gray-400" data-id="element-2514" data-mp="1qypgr5r81p64" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1" data-id="element-2515" data-mp="1gy6rtwksvihc">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-2516" data-mp="1gzq99qi6zzkp">
                  <div data-id="element-2517" data-mp="2em766un0kb5x">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2518" data-mp="2ele5obenehw8">
                      HAEMATOLOGY
                    </h4>
                    {['FBC', 'Blood Group', 'CD4 Count', 'MPS/RDT', 'AAFB', 'Viral Load'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2519" data-mp="2xdzxmkmfpobj">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2520" data-mp="30umba1v5fhy4" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2521" data-mp="1exz1oapsor0">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2522" data-mp="1e66yngbt95h">
                      CHEMISTRY
                    </h4>
                    {['Urea', 'Creatinine', 'U&E', 'FBS', 'RBS', 'Liver Function Test', 'VBG', 'ABG'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2523" data-mp="rrljyamjeiio">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2524" data-mp="10r8o346ni2ff" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2525" data-mp="20xc68x2m3c24">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2526" data-mp="20w122w5982ah">
                      SEROLOGY
                    </h4>
                    {['HIV', 'Syphilis', 'RPR', 'HBs Ag', 'HCV', 'Hepatitis Profile', 'Gravindex (Pregnancy Test)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2527" data-mp="2oo8348lxcxg8">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2528" data-mp="2aq1fdlaowdua" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2529" data-mp="1rjwuekdf9gc4">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2530" data-mp="ty8vnsthulxf">
                      ENDOCRINOLOGY
                    </h4>
                    {['Hormonal Profile', 'Testosterone', 'Anti-Müllerian Hormone (AMH)', 'Beta HCG (Quantitative)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2531" data-mp="18oydhe1gmza9">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2532" data-mp="g5jbltxizr2y" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2533" data-mp="21qppvk96svpt">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2534" data-mp="21q65zxzz7x95">
                      URINE / FERTILITY
                    </h4>
                    {['Urinalysis', 'Urine Microscopy', 'Urine Culture', 'Sperm Analysis', 'Sperm Culture', 'PAP Smear'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2535" data-mp="1i3uwrwp3ulyp">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2536" data-mp="1466ackfe36rj" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2537" data-mp="2jmeugfd7v9eh">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2538" data-mp="2jmyfw622sfx9">
                      MICROBIOLOGY / SWABS
                    </h4>
                    {['HVS', 'Urethral Swab', 'Stool Microscopy'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2539" data-mp="hums3qbt9twj">
                          <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2540" data-mp="3lcenzft2iy00" />{' '}
                          {test}
                        </label>)}
                  </div>
                  <div data-id="element-2541" data-mp="3g9fhie8urruq">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2542" data-mp="3gaqn8jluz9oh">
                      OTHER
                    </h4>
                    {['DOA 6 (Drugs of Abuse)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2543" data-mp="3p77pexc8vx83">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2544" data-mp="31c94dtuj68r" />{' '}
                        {test}
                      </label>)}
                  </div>
                </div>
                <div className="mt-6" data-id="element-2545" data-mp="lyw6prp07md0">
                  <Input label="Other Tests (Free Text)" value={otherTests} onChange={e => setOtherTests(e.target.value)} placeholder="Enter any other tests required..." data-id="element-2546" data-mp="hxalnj2bg1rq" />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-2547" data-mp="14wynxkbb8f09">
                <Button variant="outline" onClick={() => setShowLabModal(false)} data-id="element-2548" data-mp="10wokaeazq0ok">
                  Cancel
                </Button>
                <Button onClick={handleRequestLab} data-id="element-2549" data-mp="1snb57lsg3o3i">Send to Laboratory</Button>
              </div>
            </div>
          </div>}

        {/* Rad Modal */}
        {showRadModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-2550" data-mp="3rtqaksc7c7sq">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-2551" data-mp="3d5n2ndelmm1a">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2552" data-mp="3d396f186n3s5">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2553" data-mp="344f54xjwpp89">
                  Request Radiology Scan
                </h3>
                <button onClick={() => setShowRadModal(false)} data-id="element-2554" data-mp="393s3aeg8aiq5">
                  <X className="w-5 h-5 text-gray-400" data-id="element-2555" data-mp="3915mdo7g6qv9" />
                </button>
              </div>
              <div className="p-6" data-id="element-2556" data-mp="2u8lsle66tvqh">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2557" data-mp="2z4j2b1my43ua">
                  Scan Type <span className="text-red-500" data-id="element-2558" data-mp="secrb580t783">*</span>
                </label>
                <select required value={scanType} onChange={e => setScanType(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm" data-id="element-2559" data-mp="xdpfpuejimcs">
                  <option value="" data-id="element-2560" data-mp="2rml7825igval">— Select a scan —</option>
                  <option value="Pelvic Scan" data-id="element-2561" data-mp="2mvcuaw4z2rso">Pelvic Scan</option>
                  <option value="Anomaly Scan" data-id="element-2562" data-mp="2mt03rul5f940">Anomaly Scan</option>
                  <option value="OBS Scan" data-id="element-2563" data-mp="30l6l3k01fm51">OBS Scan</option>
                  <option value="Abdominal Scan" data-id="element-2564" data-mp="2wl66nzcxvaxz">Abdominal Scan</option>
                  <option value="Abdomen/Pelvis Scan" data-id="element-2565" data-mp="2wmz2ejm0kar7">
                    Abdomen and Pelvic Scan (Abdomen/Pelvis Scan)
                  </option>
                  <option value="CXR (Chest X-Ray)" data-id="element-2566" data-mp="35lb4piw8swzh">CXR (Chest X-Ray)</option>
                  <option value="HSG (Hysterosalpingography)" data-id="element-2567" data-mp="1g6ed3j423xn3">
                    HSG (Hysterosalpingography)
                  </option>
                </select>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-2568" data-mp="1b9wdexnl2phm">
                <Button variant="outline" onClick={() => setShowRadModal(false)} data-id="element-2569" data-mp="udh4ex5w94x9">
                  Cancel
                </Button>
                <Button onClick={handleRequestRad} className="bg-purple-600 hover:bg-purple-700" data-id="element-2570" data-mp="z6a599y8rt42">
                  Send to Radiology
                </Button>
              </div>
            </div>
          </div>}

        {/* Rx Modal */}
        {showRxModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto" data-id="element-2571" data-mp="gdz7tvmcwndc">
            <div className="min-h-full flex items-start sm:items-center justify-center p-4" data-id="element-2572" data-mp="lcspm08of0jb">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[calc(100vh-2rem)] flex flex-col my-4" data-id="element-2573" data-mp="1rsuzci63qjbb">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 bg-white rounded-t-lg z-10" data-id="element-2574" data-mp="1wrfy0d5pguxn">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2575" data-mp="3hc2w0a1nwf86">
                    Prescribe Medication
                  </h3>
                  <button onClick={() => setShowRxModal(false)} data-id="element-2576" data-mp="37jx4t2j8i1u1">
                    <X className="w-5 h-5 text-gray-400" data-id="element-2577" data-mp="37i3y5n8mfhm1" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto flex-1" data-id="element-2578" data-mp="32j9haqu7s34k">
                  <div className="grid grid-cols-2 gap-4" data-id="element-2579" data-mp="2xmshv0hke2c6">
                    <Input label="Drug Name" className="col-span-2" value={rxForm.drugName} onChange={e => setRxForm({
                  ...rxForm,
                  drugName: e.target.value
                })} required data-id="element-2580" data-mp="2tknq67aoffs6" />
                    <Input label="Dose" placeholder="e.g. 500mg" value={rxForm.dose} onChange={e => setRxForm({
                  ...rxForm,
                  dose: e.target.value
                })} required data-id="element-2581" data-mp="hvwlkub08xqi" />
                    <Select label="Route" options={[{
                  value: 'PO',
                  label: 'Oral (PO)'
                }, {
                  value: 'IV',
                  label: 'Intravenous (IV)'
                }, {
                  value: 'IM',
                  label: 'Intramuscular (IM)'
                }, {
                  value: 'Topical',
                  label: 'Topical'
                }, {
                  value: 'Vaginal',
                  label: 'Vaginal'
                }, {
                  value: 'Rectal',
                  label: 'Rectal'
                }]} value={rxForm.route} onChange={e => setRxForm({
                  ...rxForm,
                  route: e.target.value
                })} data-id="element-2582" data-mp="uzibkcfhib7u" />
                    <Input label="Frequency" placeholder="e.g. BD, TDS" value={rxForm.frequency} onChange={e => setRxForm({
                  ...rxForm,
                  frequency: e.target.value
                })} required data-id="element-2583" data-mp="24yb04ls528j0" />
                    <Input label="No. of Days" type="number" value={rxForm.days} onChange={e => setRxForm({
                  ...rxForm,
                  days: e.target.value
                })} required data-id="element-2584" data-mp="2vn47wqc6ln8n" />
                  </div>
                  <div className="mt-4 flex justify-end" data-id="element-2585" data-mp="2cxxn8xrtulk7">
                    <Button onClick={handleAddRx} variant="outline" className="text-green-600 border-green-600 hover:bg-green-50" data-id="element-2586" data-mp="27zcl2st0lrt4">
                      + Add to Prescription List
                    </Button>
                  </div>

                  {selectedEncounter.prescriptions.length > 0 && <div className="mt-6 border-t border-gray-200 pt-4" data-id="element-2587" data-mp="1p8lajnxajvd5">
                      <div className="flex justify-between items-center mb-2" data-id="element-2588" data-mp="4q14whpymgai">
                        <h4 className="font-medium text-sm" data-id="element-2589" data-mp="4ny555z098sl">
                          Current List ({selectedEncounter.prescriptions.length}{' '}
                          of 20)
                        </h4>
                        {selectedEncounter.prescriptions.length >= 20 && <span className="text-xs text-amber-600 font-medium" data-id="element-2590" data-mp="3vwlq3ktc2tzm">
                            Maximum reached
                          </span>}
                      </div>
                      <ul className="space-y-2 max-h-64 overflow-y-auto pr-1 border border-gray-200 rounded p-2 bg-gray-50" data-id="element-2591" data-mp="sdiz0jwp5qgn">
                        {selectedEncounter.prescriptions.map((rx, idx) => <li key={rx.id} className="text-sm bg-white p-2 rounded flex justify-between items-center border border-gray-100 gap-2" data-id="element-2592" data-mp="ijilpur9hewu" data-mpi={idx}>
                            <span className="flex-1 min-w-0 truncate" data-id="element-2593" data-mp="3jejdo0zvs2uz">
                              <span className="text-gray-400 mr-2" data-id="element-2594" data-mp="3jgmb363d4y9q">
                                {idx + 1}.
                              </span>
                              {rx.drugName} - {rx.dose}
                            </span>
                            <span className="text-gray-500 text-xs whitespace-nowrap" data-id="element-2595" data-mp="34qoga7axhabk">
                              {rx.frequency} x {rx.days} days
                            </span>
                            {rx.status === 'Pending' ? <button onClick={() => handleDeletePrescription(rx.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors flex-shrink-0" title="Remove this drug" aria-label={`Remove ${rx.drugName}`} data-id="element-2596" data-mp="19zib9pev8xi">
                                <Trash2 className="w-4 h-4" data-id="element-2597" data-mp="2d0tp053ifak9" />
                              </button> : <span className="text-xs text-gray-400 flex-shrink-0 italic" data-id="element-2598" data-mp="whs3vtk384jx">
                                {rx.status}
                              </span>}
                          </li>)}
                      </ul>
                    </div>}
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 flex-shrink-0 sticky bottom-0 rounded-b-lg z-10" data-id="element-2599" data-mp="1b6wmvcypict3">
                  <Button onClick={() => setShowRxModal(false)} className="bg-blue-600 hover:bg-blue-700 text-white" data-id="element-2600" data-mp="1k7mdm2941czy">
                    Done
                  </Button>
                </div>
              </div>
            </div>
          </div>}

        {/* Image Zoom Modal */}
        {zoomImage && <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50" onClick={() => setZoomImage(null)} data-id="element-2601" data-mp="y3u1ca0z155t">
            <div className="absolute top-4 right-4 flex items-center gap-3 z-10" data-id="element-2602" data-mp="t6twg2fuy8ga">
              <span className="text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded" data-id="element-2603" data-mp="175tir3fweb9a">
                Scroll to zoom • Click outside to close
              </span>
              <button className="text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2" onClick={() => setZoomImage(null)} data-id="element-2604" data-mp="1h1o5a9n8sqhc">
                <X className="w-6 h-6" data-id="element-2605" data-mp="1pv9ktv78ebrt" />
              </button>
            </div>
            <div className="max-w-[90vw] max-h-[85vh] overflow-auto rounded-lg" onClick={e => e.stopPropagation()} data-id="element-2606" data-mp="1ltod0o9k0noh">
              <img src={zoomImage} alt="Radiology Scan — Zoom up to 200%" className="min-w-[60vw] max-w-none" style={{
            width: '180%',
            cursor: 'grab'
          }} data-id="element-2607" data-mp="1zth089lvglrp" />
            </div>
            <p className="text-gray-400 text-xs mt-3" data-id="element-2608" data-mp="2je7p1e457ui0">
              Image displayed at up to 200% zoom. Scroll within the frame to
              pan.
            </p>
          </div>}

        {/* ─── TEST FILE MODAL (View File per investigation) ─── */}
        {viewingTest && selectedEncounter && (() => {
        const patient = getPatient(selectedEncounter.patientId);
        if (!patient) return null;
        const lab = viewingTest.kind === 'lab' ? selectedEncounter.labRequests.find(l => l.id === viewingTest.labId) : null;
        const rad = viewingTest.kind === 'rad' ? selectedEncounter.radiologyRequests.find(r => r.id === viewingTest.radId) : null;
        if (!lab && !rad) return null;
        const focusedTest = viewingTest.kind === 'lab' ? viewingTest.testName : null;
        // Filter lab.results by the requested test name when possible
        const labResultsFiltered = lab && lab.results ? focusedTest ? Object.fromEntries(Object.entries(lab.results).filter(([k]) => k.toLowerCase().includes(focusedTest.toLowerCase()) || focusedTest.toLowerCase().includes(k.toLowerCase()) ||
        // FBC sub-tests, urinalysis (UA_), testosterone — keep all if focused test is one of these umbrella tests
        ['fbc', 'urinalysis', 'testosterone', 'sperm analysis'].some(u => focusedTest.toLowerCase().includes(u)))) : lab.results : null;
        return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-2609" data-mp="3b7hujou4kfg0">
                <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-2610" data-mp="3g59un64xb7va">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-2611" data-mp="2nhxbz71vvk79">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-2612" data-mp="2x7yznxqbr0ln">
                      {lab ? `Laboratory Result — ${focusedTest || lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).join(', ')}` : `Radiology Result — ${rad?.scanType}`}
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-2613" data-mp="1eijzphf6e24o">
                      <Button variant="outline" size="sm" onClick={handlePrintTestModal} data-id="element-2614" data-mp="1jgvbvysy0oq2">
                        <Printer className="w-4 h-4 mr-1" data-id="element-2615" data-mp="19hw047cvscyu" /> Print
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleDownloadTestPdf} disabled={isGeneratingTestPdf} data-id="element-2616" data-mp="vqr9furhqv0n">
                        <Download className="w-4 h-4 mr-1" data-id="element-2617" data-mp="ltwaxbv7wumd" />
                        {isGeneratingTestPdf ? 'Generating...' : 'Download PDF'}
                      </Button>
                      <button onClick={closeTestModal} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-2618" data-mp="crdaszitgw3t">
                        <X className="w-5 h-5 text-gray-500" data-id="element-2619" data-mp="3u9sp2k11f6nv" />
                      </button>
                    </div>
                  </div>

                  <div ref={testModalRef} className="p-8 bg-white" data-id="element-2620" data-mp="3pdlqjcaz0xfy">
                    <div className="flex justify-between items-start mb-6 border-b-2 border-black pb-4" data-id="element-2621" data-mp="76nv1xqys142">
                      <div className="flex items-center" data-id="element-2622" data-mp="33hnmms483w0">
                        <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-2623" data-mp="h3sgfvczjsjt" />
                        <div data-id="element-2624" data-mp="qvoi1gea60ss">
                          <h1 className="text-xl font-bold text-pink-600 uppercase" data-id="element-2625" data-mp="lu8wzoc3ruae">
                            BMED MEDICAL CENTRE
                          </h1>
                          <p className="text-xs italic text-gray-600" data-id="element-2626" data-mp="zsh2ikn7g3x6">
                            Caring Healing Leading
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm" data-id="element-2627" data-mp="3ollwt1hyz3dr">
                        <p className="font-bold uppercase border-b border-black pb-1 mb-2" data-id="element-2628" data-mp="3tnbakowktv5j">
                          {lab ? 'Laboratory Report' : 'Radiology Report'}
                        </p>
                        <p data-id="element-2629" data-mp="6a8yt59tmvrh">
                          <span className="font-semibold" data-id="element-2630" data-mp="6bjzuxbvtpew">Patient:</span>{' '}
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p data-id="element-2631" data-mp="g6bwnyxn488m">
                          <span className="font-semibold" data-id="element-2632" data-mp="g9q07ai1inhn">File No:</span>{' '}
                          {patient.bmcFileNumber}
                        </p>
                        <p data-id="element-2633" data-mp="p7a4ksb78zwh">
                          <span className="font-semibold" data-id="element-2634" data-mp="p7ii8mf5c98q">Age/Sex:</span>{' '}
                          {patient.age} / {patient.sex}
                        </p>
                        <p data-id="element-2635" data-mp="2qywns7ounqie">
                          <span className="font-semibold" data-id="element-2636" data-mp="2r06o73j6das7">Date:</span>{' '}
                          {new Date((lab?.completedAt || rad?.completedAt) ?? Date.now()).toLocaleString()}
                        </p>
                        {lab?.performedBy && <p data-id="element-2637" data-mp="25oqwbhk1mt0j">
                            <span className="font-semibold" data-id="element-2638" data-mp="25psb9x46jznj">Performed By:</span>{' '}
                            {lab.performedBy}
                          </p>}
                      </div>
                    </div>

                    {lab && <div data-id="element-2639" data-mp="2te34qf7jqx1s">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-2640" data-mp="2tda8472axnxd">
                          {focusedTest || 'Tests Performed'}
                        </h2>

                        {/* ─── Structured FBC Form ─── */}
                        {focusedTest && isFBCTest(focusedTest) ? <div className="space-y-4" data-id="element-2641" data-mp="1cztqidx0qooo">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2642" data-mp="1d2g0ek7dtga2">
                              <h3 className="font-bold uppercase" data-id="element-2643" data-mp="30uom4k574vxr">
                                Full Blood Count (FBC) Report
                              </h3>
                            </div>
                            {fbcStructure.map(cat => <div key={cat.category} className="border border-black" data-id="element-2644" data-mp="2vyq196j2tw23">
                                <div className="bg-gray-100 p-2 font-bold border-b border-black text-xs uppercase" data-id="element-2645" data-mp="3omvl7r2b3h0v">
                                  {cat.category}
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-2646" data-mp="3fkw6x6ioer1m">
                                  <thead data-id="element-2647" data-mp="1z7p5yacjsmly">
                                    <tr className="bg-gray-50" data-id="element-2648" data-mp="1z5ul0w1a0uhv">
                                      <th className="border border-black p-1.5 text-left" data-id="element-2649" data-mp="2471ur0mzc3r6">
                                        Test
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-2650" data-mp="1rnwqbxnr7nyp">
                                        Result
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-2651" data-mp="25lbivb9b0buw">
                                        Reference Range
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-24" data-id="element-2652" data-mp="1cwntp4lfp2c2">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-2653" data-mp="1hxbl3rdkrl6t">
                                    {cat.tests.map(t => <tr key={t.name} data-id="element-2654" data-mp="u6xzojphwkl7">
                                        <td className="border border-black p-1.5" data-id="element-2655" data-mp="p9xu09wk5jum">
                                          {t.name}
                                        </td>
                                        <td className="border border-black p-1.5 font-bold" data-id="element-2656" data-mp="2uas662ib2g4n">
                                          {lab.results && lab.results[t.name] || '—'}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2657" data-mp="26d2yuzv2ocr6">
                                          {t.range}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2658" data-mp="2fdhmi5vie3xu">
                                          {t.unit}
                                        </td>
                                      </tr>)}
                                  </tbody>
                                </table>
                              </div>)}
                          </div> : focusedTest && isUrinalysisTest(focusedTest) ? <div data-id="element-2659" data-mp="4qp6uuipvm6n">
                            <div className="text-center border-b-2 border-black pb-2 mb-6" data-id="element-2660" data-mp="4sig8hkpskic">
                              <h3 className="font-bold uppercase" data-id="element-2661" data-mp="3w4k2s2fvuavr">
                                Urinalysis Report
                              </h3>
                            </div>
                            <div className="max-w-md mx-auto space-y-2" data-id="element-2662" data-mp="3n5ggkplf5fd1">
                              {urinalysisTests.map(t => {
                      const key = `UA_${t.name}`;
                      const value = lab.results && (lab.results[key] || lab.results[t.name]) || '';
                      return <div key={t.name} className="flex items-center text-sm" data-id="element-2663" data-mp="17dsnj59j06zi">
                                    <span className="w-16 font-bold" data-id="element-2664" data-mp="1uonii1wzu4m1">
                                      {t.name}
                                    </span>
                                    <span className="mx-3 text-gray-400" data-id="element-2665" data-mp="1gv6a9d9rcebb">
                                      -
                                    </span>
                                    <span className="flex-1 border-b border-gray-400 pb-0.5 font-medium" data-id="element-2666" data-mp="16y1m57wcq8rv">
                                      {value || '\u00A0'}
                                    </span>
                                  </div>;
                    })}
                            </div>
                            <div className="max-w-md mx-auto mt-10 grid grid-cols-2 gap-4 text-sm" data-id="element-2667" data-mp="12z2j5su6km32">
                              <div data-id="element-2668" data-mp="2sabhmuku3119">
                                <p className="font-semibold mb-2" data-id="element-2669" data-mp="2xbip0sj2dy7e">
                                  Dr.'s Name:
                                </p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-2670" data-mp="37d35dt51sikn">
                                  {lab.results && lab.results['UA_DR_NAME'] || '\u00A0'}
                                </p>
                              </div>
                              <div data-id="element-2671" data-mp="3bfhngb0w7eav">
                                <p className="font-semibold mb-2" data-id="element-2672" data-mp="3q5f5e988b7rm">Signature:</p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-2673" data-mp="3q7i5xn70cjal">
                                  {lab.results && lab.results['UA_DR_SIG'] || '\u00A0'}
                                </p>
                              </div>
                            </div>
                          </div> : focusedTest && isTestosteroneTest(focusedTest) ? <div className="space-y-2" data-id="element-2674" data-mp="20r90k0rr3qgt">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2675" data-mp="20sttxn2bx3d3">
                              <h3 className="font-bold uppercase" data-id="element-2676" data-mp="g01pfvh18s4v">
                                Testosterone Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2677" data-mp="p2196vjfmabi">
                              <thead data-id="element-2678" data-mp="kxtm12smh2fe">
                                <tr className="bg-gray-100" data-id="element-2679" data-mp="l0z5o2ihfc3q">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2680" data-mp="yxcr1mq4zxnh">
                                    Test
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2681" data-mp="18o7aykfwul5r">
                                    Result
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2682" data-mp="13rqff56dt4in">
                                    Reference (Male)
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2683" data-mp="1crvkh8o3rqtn">
                                    Reference (Female)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2684" data-mp="219maoasa3e4q">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2685" data-mp="1dlcqteeml0g8">
                                {testosteroneTests.map(t => <tr key={t.name} data-id="element-2686" data-mp="1ic2zb0icljf5">
                                    <td className="border border-black p-1.5" data-id="element-2687" data-mp="1idnrwkl3qsad">
                                      {t.name}
                                    </td>
                                    <td className="border border-black p-1.5 font-bold" data-id="element-2688" data-mp="18li0pd2ocew8">
                                      {lab.results && (lab.results[`TESTO_${t.name}`] || lab.results[t.name]) || '—'}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2689" data-mp="2wk0wsabfhozv">
                                      {t.rangeMale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2690" data-mp="2riv4gdlok3qv">
                                      {t.rangeFemale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2691" data-mp="3k93e1h25hftu">
                                      {t.unit}
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>
                          </div> : focusedTest && isHormonalProfileTest(focusedTest) ? <div data-id="element-2692" data-mp="1uu5k8gmkyz1q">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2693" data-mp="1ut3wpcodok34">
                              <h3 className="font-bold uppercase" data-id="element-2694" data-mp="adxor1ermq1l">
                                Hormonal Profile Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2695" data-mp="jc9njqpscu54">
                              <thead data-id="element-2696" data-mp="o4krd4nnd02y">
                                <tr className="bg-gray-100" data-id="element-2697" data-mp="o5m032h6xahi">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2698" data-mp="t3xb3gj81r1b">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2699" data-mp="131l6m8onj2kj">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2700" data-mp="170swm8gntay9">
                                    Reference Range
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2701" data-mp="38us74rlyjo9w">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2702" data-mp="2ni9lpi1en9sa">
                                {hormonalProfileTests.map(h => <Fragment key={h.key} data-id="element-2703" data-mp="2ijg71madtcqj">
                                    <tr className="align-top" data-id="element-2704" data-mp="2ijzkapp37cy8">
                                      <td className="border border-black p-1.5 font-medium" data-id="element-2705" data-mp="1uunhc7kcs14d">
                                        {h.name}
                                      </td>
                                      <td className="border border-black p-1.5 font-bold" data-id="element-2706" data-mp="24jx5jcc2szn7">
                                        {lab.results && (lab.results[`HP_${h.key}`] || lab.results[h.name] || lab.results[h.key]) || '—'}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-2707" data-mp="3iq227nh85f7u">
                                        {h.ranges.map(r => <div key={r.label} data-id="element-2708" data-mp="39rhnexesbpow">
                                            <span className="font-medium" data-id="element-2709" data-mp="39ovi74fd9m9u">
                                              {r.label}:
                                            </span>{' '}
                                            {r.value}
                                          </div>)}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-2710" data-mp="3sgodx6ltlll9">
                                        {h.unit}
                                      </td>
                                    </tr>
                                    <tr data-id="element-2711" data-mp="2h4dq7elxofno">
                                      <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2712" data-mp="2m1dvhnayledq">
                                        Method: {h.method}
                                      </td>
                                    </tr>
                                  </Fragment>)}
                              </tbody>
                            </table>
                          </div> : focusedTest && isAMHTest(focusedTest) ? <div data-id="element-2713" data-mp="3xe3lgbwu872">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2714" data-mp="3v1ch8jse86dy">
                              <h3 className="font-bold uppercase" data-id="element-2715" data-mp="3r00sshl99r7c">
                                Anti Mullerian Hormone (AMH) Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2716" data-mp="1p8xenic5pui1">
                              <thead data-id="element-2717" data-mp="1p6tcp5xtj5ke">
                                <tr className="bg-gray-100" data-id="element-2718" data-mp="3772g1i2man56">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2719" data-mp="374g6jcw61xkb">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2720" data-mp="3gwvoia7gqm57">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2721" data-mp="2o8q5brw2557h">
                                    Reference Range (by age, ng/mL)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2722" data-mp="2x71r9glzbetl">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2723" data-mp="ck8coyklitsi">
                                <tr className="align-top" data-id="element-2724" data-mp="hg5u787li66v">
                                  <td className="border border-black p-1.5 font-medium" data-id="element-2725" data-mp="hirzt2axebmg">
                                    {amhStructure.name}
                                  </td>
                                  <td className="border border-black p-1.5 font-bold" data-id="element-2726" data-mp="3kcj20hainfdq">
                                    {lab.results && (lab.results[amhStructure.storageKey] || lab.results[focusedTest] || lab.results['AMH'] || lab.results['Anti-Müllerian Hormone'] || lab.results['Anti Mullerian Hormone (AMH)']) || '—'}
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-2727" data-mp="2mnfzvk6a10dg">
                                    {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-2728" data-mp="28gvyf9ceyyan">
                                        <span className="font-medium" data-id="element-2729" data-mp="28jidrv5jqi3f">
                                          {a.ageBand}:
                                        </span>{' '}
                                        {a.range}
                                      </div>)}
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-2730" data-mp="1ot58qdo4m2ri">
                                    {amhStructure.unit}
                                  </td>
                                </tr>
                                <tr data-id="element-2731" data-mp="1fu1q1asvfpdj">
                                  <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2732" data-mp="1fv329ilmihwu">
                                    Method: {amhStructure.method}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                            <div className="mt-3 border border-amber-300 bg-amber-50 rounded p-3 text-xs" data-id="element-2733" data-mp="ps8uu62os1l0">
                              <p className="font-bold uppercase mb-1" data-id="element-2734" data-mp="upigrbqv9v43">
                                Interpretation
                              </p>
                              <ul className="list-disc pl-5 space-y-0.5 text-gray-700" data-id="element-2735" data-mp="13im3ybrbdmbx">
                                {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-2736" data-mp="18fucdkmnubzz" data-mpi={i}>{line}</li>)}
                              </ul>
                            </div>
                          </div> : focusedTest && isHCGTest(focusedTest) ? <div data-id="element-2737" data-mp="3f8wsbpbuoxk4">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2738" data-mp="3f9psu8k7uqtt">
                              <h3 className="font-bold uppercase" data-id="element-2739" data-mp="3k7rixdg27qes">
                                Beta HCG (Quantitative) Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2740" data-mp="1wjtxmz1kgqce">
                              <thead data-id="element-2741" data-mp="1skuvtnb4t9p6">
                                <tr className="bg-gray-100" data-id="element-2742" data-mp="1shp5y5uoi3se">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2743" data-mp="1nlro1v3ropdi">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2744" data-mp="2g8umyoccv24a">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2745" data-mp="26b6p3pjgdehw">
                                    Reference Range (mIU/mL)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2746" data-mp="zt1hy2ijp0b5">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2747" data-mp="11cjlkdikc5fu">
                                {(() => {
                        const hcgValue = lab.results && (lab.results[hcgStructure.storageKey] || lab.results[focusedTest] || lab.results['HCG'] || lab.results['Beta HCG']) || '';
                        const abnormal = isHCGAbnormal(hcgValue);
                        return <>
                                      <tr className="align-top" data-id="element-2748" data-mp="8l9q0ajjgmgd">
                                        <td className="border border-black p-1.5 font-medium" data-id="element-2749" data-mp="3qtqy95pztbpa">
                                          {hcgStructure.name}
                                        </td>
                                        <td className={`border border-black p-1.5 font-bold ${abnormal ? 'text-red-600 bg-red-50' : ''}`} data-id="element-2750" data-mp="3c71xz1nx2uhu">
                                          {hcgValue || '—'}
                                          {abnormal && <div className="text-xs font-normal mt-0.5" data-id="element-2751" data-mp="3v54jh6h9a9aw">
                                              Above normal threshold (&gt; 6
                                              mIU/mL)
                                            </div>}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2752" data-mp="3ric4npksect">
                                          {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-2753" data-mp="dpxsbvn2e37j">
                                              <span className="font-medium" data-id="element-2754" data-mp="277mz8fxcdpq1">
                                                {r.label}:
                                              </span>{' '}
                                              {r.range}
                                            </div>)}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2755" data-mp="2lyccdnud5ewj">
                                          {hcgStructure.unit}
                                        </td>
                                      </tr>
                                      <tr data-id="element-2756" data-mp="1y6p3ekx24oij">
                                        <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2757" data-mp="1y91lcy2teyvl">
                                          Method: {hcgStructure.method}
                                        </td>
                                      </tr>
                                    </>;
                      })()}
                              </tbody>
                            </table>
                          </div> : labResultsFiltered && Object.keys(labResultsFiltered).length > 0 ? <table className="w-full border-collapse text-sm" data-id="element-2758" data-mp="ja4cenm4tmox">
                            <thead data-id="element-2759" data-mp="eb15exmld6eb">
                              <tr className="bg-gray-100" data-id="element-2760" data-mp="e9zvwxl84ryp">
                                <th className="border border-black p-2 text-left" data-id="element-2761" data-mp="a8wfa8vcqrwf">
                                  Parameter
                                </th>
                                <th className="border border-black p-2 text-left" data-id="element-2762" data-mp="5bxq84qjpb53">
                                  Result
                                </th>
                              </tr>
                            </thead>
                            <tbody data-id="element-2763" data-mp="2isqwbn89mh2h">
                              {Object.entries(labResultsFiltered).map(([k, v]) => <tr key={k} data-id="element-2764" data-mp="2nl0nt7p13p6v">
                                    <td className="border border-black p-2 font-medium" data-id="element-2765" data-mp="29sayataak250">
                                      {k}
                                    </td>
                                    <td className="border border-black p-2" data-id="element-2766" data-mp="2eorl9k5r8agi">
                                      {v}
                                    </td>
                                  </tr>)}
                            </tbody>
                          </table> : <p className="text-gray-500 italic" data-id="element-2767" data-mp="2xgc1dmopaqd1">
                            No structured results recorded for this test.
                          </p>}

                        {lab.labComments && <div className="mt-6 border border-gray-300 bg-gray-50 rounded p-4" data-id="element-2768" data-mp="1m03vpcdz2yx5">
                            <h3 className="font-bold uppercase mb-2 text-sm" data-id="element-2769" data-mp="1eks7eablu1um">
                              Comments / Observations / Interpretation
                            </h3>
                            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-id="element-2770" data-mp="1niuf18pufsqv">
                              {lab.labComments}
                            </pre>
                          </div>}
                      </div>}

                    {rad && <div data-id="element-2771" data-mp="qrjrsjepm22e">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-2772" data-mp="14lb2vw839u08">
                          {rad.scanType}
                        </h2>
                        {rad.radiographerName && <p className="text-sm mb-3" data-id="element-2773" data-mp="c4716ga1p655">
                            <span className="font-semibold" data-id="element-2774" data-mp="c2np71vf9h96">
                              Sonographer / Radiographer:
                            </span>{' '}
                            {rad.radiographerName}
                          </p>}
                        {rad.report && <div className="mb-4 border border-black p-4" data-id="element-2775" data-mp="3msmckn9cenwf">
                            <pre className="whitespace-pre-wrap font-sans text-sm" data-id="element-2776" data-mp="3routo673or68">
                              {rad.report}
                            </pre>
                          </div>}
                        {rad.images && rad.images.length > 0 && <div data-id="element-2777" data-mp="9iyepbmvp9jg">
                            <div className="flex justify-between items-center mb-3 print:hidden" data-id="element-2778" data-mp="9i6qilvm2e37">
                              <h3 className="font-bold" data-id="element-2779" data-mp="ebhzotnwb6ya">Scan Images</h3>
                              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg" data-id="element-2780" data-mp="j8hxyiei75eu">
                                <button onClick={() => setTestImageZoom(z => Math.max(10, z - 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom out" data-id="element-2781" data-mp="oah4201mogfh">
                                  <ZoomOut className="w-4 h-4" data-id="element-2782" data-mp="1yt0pli39nbcv" />
                                </button>
                                <input type="range" min={10} max={200} step={10} value={testImageZoom} onChange={e => setTestImageZoom(Number(e.target.value))} className="w-32" data-id="element-2783" data-mp="2wh2fw8io0zud" />
                                <button onClick={() => setTestImageZoom(z => Math.min(200, z + 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom in" data-id="element-2784" data-mp="3af94ey1q5nhd">
                                  <ZoomIn className="w-4 h-4" data-id="element-2785" data-mp="qybubum7avrb" />
                                </button>
                                <span className="text-sm font-medium w-12 text-right" data-id="element-2786" data-mp="1jkmsyzq8ymlh">
                                  {testImageZoom}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-4 print:space-y-2" data-id="element-2787" data-mp="19ts89zsnfv25">
                              {rad.images.map((img, idx) => <div key={idx} className="border-2 border-gray-300 rounded overflow-auto bg-gray-50 max-h-[600px] print:max-h-none print:overflow-visible" data-id="element-2788" data-mp="27cbrwyxkq9be" data-mpi={idx}>
                                  <div style={{
                        transform: `scale(${testImageZoom / 100})`,
                        transformOrigin: 'top left',
                        display: 'inline-block'
                      }} data-id="element-2789" data-mp="2eu9x4r8q2y8t">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="block" data-id="element-2790" data-mp="2xrubv5hrwgfp" />
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </div>
              </div>;
      })()}
      </div>;
  }
  return <div className="space-y-6" data-id="element-2791" data-mp="1cgk4mc8zs88f">
      <div className="flex justify-between items-center" data-id="element-2792" data-mp="1cd6479jw293m">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2793" data-mp="1q7p7dn7npgmz">
          Clinician Dashboard
        </h1>
        <div className="flex space-x-2" data-id="element-2794" data-mp="1lew1ux4d649u">
          <Badge variant="info" className="text-sm px-3 py-1" data-id="element-2795" data-mp="1le47tqqf0elo">
            {pendingEncounters.length} Patients Waiting
          </Badge>
          <Badge variant="success" className="text-sm px-3 py-1 bg-green-100 text-green-800" data-id="element-2796" data-mp="1v6a257453841">
            {completedEncounters.length} Completed
          </Badge>
        </div>
      </div>

      <div className="border-b border-gray-200" data-id="element-2797" data-mp="247qal9be905m">
        <nav className="-mb-px flex space-x-8" data-id="element-2798" data-mp="eriipjoz1b6v">
          <button onClick={() => setActiveTab('queue')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'queue' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2799" data-mp="esa2zyd4bmhu">
            Active Queue
          </button>
          <button onClick={() => setActiveTab('completed')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'completed' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2800" data-mp="2cnfidfyjvfle">
            Completed Consultations / History
          </button>
        </nav>
      </div>

      {activeTab === 'queue' && <Card noPadding data-id="element-2801" data-mp="1tpmqd8pgsq2s">
          <ul className="divide-y divide-gray-200" data-id="element-2802" data-mp="1tq5y5wlehy33">
            {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          const isInvestigations = encounter.stage === 'INVESTIGATIONS';
          const hasResults = isInvestigations && !encounter.pendingLab && !encounter.pendingRadiology;
          const hasDraft = !!encounter.clinicalNote;
          const isPharmacy = encounter.stage === 'PHARMACY';
          const iconBg = hasResults ? 'bg-green-100' : isPharmacy ? 'bg-pink-100' : isInvestigations ? 'bg-yellow-100' : 'bg-blue-100';
          const iconColor = hasResults ? 'text-green-600' : isPharmacy ? 'text-pink-600' : isInvestigations ? 'text-yellow-600' : 'text-blue-600';
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-2803" data-mp="20pexirt9q6l8">
                    <div className="flex items-center" data-id="element-2804" data-mp="c7lum6roubia">
                      <div className={`p-2 rounded-full mr-4 ${iconBg}`} data-id="element-2805" data-mp="c95avxdcgmk2">
                        <Stethoscope className={`h-6 w-6 ${iconColor}`} data-id="element-2806" data-mp="g7cuczz701yy" />
                      </div>
                      <div data-id="element-2807" data-mp="l8a7uz99c7u4">
                        <p className="text-sm font-medium text-blue-600" data-id="element-2808" data-mp="l5xsmtuvwbkr">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-2809" data-mp="3opiojfs911n9">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1" data-id="element-2810" data-mp="2h98ia486aa1">
                          <ConsultationTypeBadge type={encounter.consultationType} size="sm" data-id="element-2811" data-mp="2fy4c96vb0ie" />
                          <Badge variant="default" data-id="element-2812" data-mp="35x6nqxh8u8f8">{patient.age} yrs</Badge>
                          {hasResults && <Badge variant="success" data-id="element-2813" data-mp="3av82x7bjtnlf">Results Ready</Badge>}
                          {isInvestigations && encounter.pendingLab && <Badge variant="warning" data-id="element-2814" data-mp="2yu05c8q33s6v">Awaiting Lab</Badge>}
                          {isInvestigations && encounter.pendingRadiology && <Badge variant="warning" data-id="element-2815" data-mp="37mtpokke1hrl">Awaiting Radiology</Badge>}
                          {isPharmacy && <Badge variant="purple" data-id="element-2816" data-mp="2jzc2l2c2j7uy">At Pharmacy</Badge>}
                          {hasDraft && encounter.stage === 'CLINICIAN' && <Badge variant="info" data-id="element-2817" data-mp="2tx9ohj6ggske">Draft Saved</Badge>}
                        </div>
                      </div>
                    </div>
                    <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-2818" data-mp="94en9yoicizo">
                      Open File
                    </Button>
                  </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-2819" data-mp="1m385yjv1jw2l">
                <Stethoscope className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-2820" data-mp="1r3m151bjnbj7" />
                <p className="text-lg font-medium text-gray-900" data-id="element-2821" data-mp="1d3b9hw2etswz">
                  No patients waiting
                </p>
              </li>}
          </ul>
        </Card>}

      {activeTab === 'completed' && <Card data-id="element-2822" data-mp="25rql08mc1t3k">
          <div className="mb-4" data-id="element-2823" data-mp="25ta384rjue7z">
            <input type="text" placeholder="Search completed consultations by patient name or file number..." className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2824" data-mp="25u3cpdhw7htt" />
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2825" data-mp="2opjpvggiod3l">
            <ul className="divide-y divide-gray-200" data-id="element-2826" data-mp="2ticrvwklp7bz">
              {filteredCompleted.length > 0 ? filteredCompleted.map(encounter => {
            const patient = getPatient(encounter.patientId);
            if (!patient) return null;
            return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-2827" data-mp="jq1uljd4zqf6">
                      <div className="flex items-center" data-id="element-2828" data-mp="1cefrfvshogp5">
                        <div className="p-2 rounded-full mr-4 bg-green-100" data-id="element-2829" data-mp="1cbtknydfa57z">
                          <CheckCircle className="h-6 w-6 text-green-600" data-id="element-2830" data-mp="17fw9s7kxhr2l" />
                        </div>
                        <div data-id="element-2831" data-mp="12i30h5obwgy3">
                          <p className="text-sm font-medium text-blue-600" data-id="element-2832" data-mp="yeoiwakqrsij">
                            {patient.bmcFileNumber}
                          </p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-2833" data-mp="3n7k3zv8k55hp">
                            {patient.firstName} {patient.lastName}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-1 text-sm text-gray-500" data-id="element-2834" data-mp="1ynmkpsrw6ixl">
                            <span data-id="element-2835" data-mp="22p85e0ao41ic">
                              {new Date(encounter.startedAt).toLocaleDateString()}
                            </span>
                            <span data-id="element-2836" data-mp="2cfknllv0filb">•</span>
                            <span data-id="element-2837" data-mp="2hd30fnc5ehyl">
                              Attended by:{' '}
                              {encounter.lastAttendingClinician || 'Unknown'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-2838" data-mp="2qbobyyj7cwpv">
                        Open File
                      </Button>
                    </li>;
          }) : <li className="p-12 text-center text-gray-500" data-id="element-2839" data-mp="10ykw2sghl7sc">
                  <p className="text-lg font-medium text-gray-900" data-id="element-2840" data-mp="10z2yy7xnmk98">
                    No completed consultations found
                  </p>
                </li>}
            </ul>
          </div>
        </Card>}
    </div>;
};